import ProductServiceAPI from '../../../api/ProductServiceAPI';
import { engineFigures } from '../../../utils/tariffFigures';
import FileAPI from '../../../api/FileAPI';
import {
  createComment,
  getCommentsByCommentableId,
  deleteComment,
} from '../../../api/bitedb/comment/comment.service';
import type {
  ImportIssueType,
  ImportProduct,
  ImportRisk,
  PgaAgency,
} from './import-types';
import type { AppliedPgaFlag } from './pga-applied';
import type { PgaCode } from './pga-schema';
import type {
  ProductDetail,
  CrossRuling,
  ChildComponent,
  DutyLine,
  FtaItem,
  ExclusionItem,
  BiteLensGroup,
} from './import-detail';

// Parses a stored tariff-calculator rate STRING (e.g. "25%", "Free",
// "0.8¢ each + 5.4%") into a percentage number. Specific/compound components
// (the "0.8¢ each" part) aren't captured — a documented approximation, since
// per-row duty dollars aren't persisted (only the rate string + the line's
// dollar base are), matching the same gap the legacy calculator's own
// per-row recompute has for non-ad-valorem rates.
const parseRatePct = (rate: unknown): number => {
  if (typeof rate !== 'string') return 0;
  const match = rate.match(/(-?\d+(?:\.\d+)?)\s*%/);
  return match ? parseFloat(match[1]) : 0;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
      ? Number(value)
      : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Stored math.totalCost includes MPF and HMF; Import Hub totals exclude both
// fees (declared value + duty). The fees stay in the stored tariff result.
export const feeFreeTotalCost = (math: any): number | undefined => {
  const total = toFiniteNumber(math?.totalCost);
  if (total === undefined) return undefined;
  return (
    total - (toFiniteNumber(math?.mpf) ?? 0) - (toFiniteNumber(math?.hmf) ?? 0)
  );
};

const isPureAdValoremRate = (rate: unknown): boolean =>
  typeof rate === 'string' && /^\s*-?\d+(?:\.\d+)?\s*%\s*$/.test(rate);

// Ports the legacy screening tool's HTS normalization (Commodities.tsx
// formatHTS): strips to digits, requires exactly 6/8/10 of them, and
// re-groups as 4.2 / 4.2.2 / 4.2.2.2 — the dotted shape the commodity index's
// HS-code prefix matching expects. Returns null (caller falls back to the
// raw string) when the code isn't one of those three lengths.
export const normalizeHtsForSearch = (
  hts: string | null | undefined,
): string | null => {
  const digits = (hts ?? '').replace(/\D/g, '');
  if (![6, 8, 10].includes(digits.length)) return null;
  return [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ]
    .filter(Boolean)
    .join('.');
};

/**
 * Shape returned by GET /database/group-id/:groupId (biteservices product
 * service — see database.service.ts#getDatabasesByGroupId). Limited to the
 * columns that query actually selects; only `id`/`name` are guaranteed.
 */
export interface ImportServiceDatabase {
  id: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  accent_color?: string | null;
  type?: string | null;
  subtype?: string | null;
  database_structure?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  screening_frequency?: string | null;
  screening_day_of_week?: string | null;
  notifications_email?: string | null;
  notifications_enabled?: boolean | null;
  screening_lists?: string[] | null;
  last_screen_timestamp?: string | null;
  compliance_score?: number | null;
  nr_products?: number;
  total_issues_products?: number;
  nr_high_risk?: number;
  nr_high_risk_products?: number;
}

export const IMPORT_PRODUCT_SCREENING_LISTS = [
  'AD CVD Commodities',
  'AD CVD Entities',
  'Consumer Protection',
  'ESG Commodities',
  'Forced Labor Commodities',
  'Nomenclatures - HTS Codes Schedule B and CN Codes',
  'PGA Codes',
  'Sensitive Technology',
  'US Duties & Tariffs',
  'US Monitoring and Regulations',
] as const;

export interface ImportServiceDatabasesResult {
  success: boolean;
  databases: ImportServiceDatabase[];
  total: number;
  page: number;
  pageSize: number;
  error?: string;
}

export const fetchImportDatabasesByGroupId = async (
  groupId: string,
  page = 1,
  pageSize = 12,
  signal?: AbortSignal,
): Promise<ImportServiceDatabasesResult> => {
  // Scope to 'products'-type databases only — the shared "Database" table also
  // holds entity ("people & companies") and tariff databases from other tools,
  // and the group-id endpoint has no filter of its own (unlike the legacy
  // ProductDB.tsx tool, which passes the same type='products' filter to its
  // own paginated database list).
  const { success, result } = await ProductServiceAPI.getDatabasesByGroupId(
    groupId,
    'products',
    {
      page,
      limit: pageSize,
      view: 'cards',
      sortBy: 'created_at',
      sortOrder: 'desc',
    },
    signal,
  );

  if (!success) {
    return {
      success: false,
      databases: [],
      total: 0,
      page,
      pageSize,
      error: typeof result === 'string' ? result : 'Failed to load databases',
    };
  }

  return {
    success: true,
    databases: Array.isArray(result?.databases) ? result.databases : [],
    total: Number(result?.total ?? 0),
    page: Number(result?.page ?? page),
    pageSize: Number(result?.limit ?? pageSize),
  };
};

export const fetchImportDatabaseListItemsByGroupId = async (
  groupId: string,
): Promise<ImportServiceDatabasesResult> => {
  const { success, result } =
    await ProductServiceAPI.getDatabaseListItemsByGroupId(groupId, 'products');
  if (!success) {
    return {
      success: false,
      databases: [],
      total: 0,
      page: 1,
      pageSize: 0,
      error: typeof result === 'string' ? result : 'Failed to load databases',
    };
  }
  const databases = Array.isArray(result) ? result : [];
  return {
    success: true,
    databases,
    total: databases.length,
    page: 1,
    pageSize: databases.length,
  };
};

export interface ImportServiceDatabaseResult {
  success: boolean;
  database: ImportServiceDatabase | null;
  error?: string;
}

export type ImportSavedViewColumn =
  | 'database'
  | 'hts'
  | 'origin'
  | 'value'
  | 'baseDuty'
  | 'addDuty'
  | 'flags'
  | 'score';

export interface ImportDatabaseSavedViewFilters {
  q: string;
  risk: string[];
  pga: string[];
  tariff: string[];
  valueBucket: string[];
  fta: string[];
  origin: string[];
  addBucket: string[];
  scoreBucket: string[];
  classification: string[];
  issueType: string[];
  hierarchy?: string[];
}

export interface ImportDatabaseSavedViewConfiguration {
  version: 1;
  filters: ImportDatabaseSavedViewFilters;
  hiddenColumns: ImportSavedViewColumn[];
}

export interface ImportDatabaseSavedView {
  id: string;
  databaseId: string;
  name: string;
  configuration: ImportDatabaseSavedViewConfiguration;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  lastUpdated: string;
  canManage: boolean;
}

export interface ImportDatabaseSavedViewsResult {
  success: boolean;
  views: ImportDatabaseSavedView[];
  error?: string;
}

export interface ImportDatabaseSavedViewResult {
  success: boolean;
  view: ImportDatabaseSavedView | null;
  error?: string;
}

const savedViewStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const SAVED_VIEW_COLUMNS: ImportSavedViewColumn[] = [
  'database',
  'hts',
  'origin',
  'value',
  'baseDuty',
  'addDuty',
  'flags',
  'score',
];

const toImportDatabaseSavedView = (
  row: any,
): ImportDatabaseSavedView | null => {
  if (!row?.id || !row?.database_id || !row?.name) return null;
  const configuration =
    row.configuration &&
    typeof row.configuration === 'object' &&
    !Array.isArray(row.configuration)
      ? row.configuration
      : {};
  const filters =
    configuration.filters &&
    typeof configuration.filters === 'object' &&
    !Array.isArray(configuration.filters)
      ? configuration.filters
      : {};
  return {
    id: String(row.id),
    databaseId: String(row.database_id),
    name: String(row.name),
    configuration: {
      version: 1,
      filters: {
        q: typeof filters.q === 'string' ? filters.q : '',
        risk: savedViewStringArray(filters.risk),
        pga: savedViewStringArray(filters.pga),
        tariff: savedViewStringArray(filters.tariff),
        valueBucket: savedViewStringArray(filters.valueBucket),
        fta: savedViewStringArray(filters.fta),
        origin: savedViewStringArray(filters.origin),
        addBucket: savedViewStringArray(filters.addBucket),
        scoreBucket: savedViewStringArray(filters.scoreBucket),
        classification: savedViewStringArray(filters.classification),
        issueType: savedViewStringArray(filters.issueType),
        hierarchy: savedViewStringArray(filters.hierarchy),
      },
      hiddenColumns: savedViewStringArray(configuration.hiddenColumns).filter(
        (column): column is ImportSavedViewColumn =>
          SAVED_VIEW_COLUMNS.includes(column as ImportSavedViewColumn),
      ),
    },
    createdBy: String(row.created_by ?? ''),
    createdByName:
      typeof row.created_by_name === 'string' ? row.created_by_name : null,
    createdAt: String(row.created_at ?? ''),
    lastUpdated: String(row.last_updated ?? ''),
    canManage: row.can_manage === true,
  };
};

export const fetchImportDatabaseSavedViews = async (
  databaseId: string,
): Promise<ImportDatabaseSavedViewsResult> => {
  const { success, result } = await ProductServiceAPI.getDatabaseSavedViews(
    databaseId,
  );
  if (!success) {
    return {
      success: false,
      views: [],
      error: typeof result === 'string' ? result : 'Failed to load saved views',
    };
  }
  return {
    success: true,
    views: Array.isArray(result)
      ? result
          .map(toImportDatabaseSavedView)
          .filter((view): view is ImportDatabaseSavedView => view !== null)
      : [],
  };
};

export const createImportDatabaseSavedView = async (
  databaseId: string,
  name: string,
  configuration: ImportDatabaseSavedViewConfiguration,
): Promise<ImportDatabaseSavedViewResult> => {
  const { success, result } = await ProductServiceAPI.createDatabaseSavedView(
    databaseId,
    { name: name, configuration: configuration },
  );
  const view = success ? toImportDatabaseSavedView(result) : null;
  return {
    success: success && view !== null,
    view,
    error:
      success && view
        ? undefined
        : typeof result === 'string'
        ? result
        : 'Failed to save view',
  };
};

export const updateImportDatabaseSavedView = async (
  databaseId: string,
  savedViewId: string,
  update: {
    name?: string;
    configuration?: ImportDatabaseSavedViewConfiguration;
  },
): Promise<ImportDatabaseSavedViewResult> => {
  const { success, result } = await ProductServiceAPI.updateDatabaseSavedView(
    databaseId,
    savedViewId,
    update,
  );
  const view = success ? toImportDatabaseSavedView(result) : null;
  return {
    success: success && view !== null,
    view,
    error:
      success && view
        ? undefined
        : typeof result === 'string'
        ? result
        : 'Failed to update saved view',
  };
};

export const deleteImportDatabaseSavedView = async (
  databaseId: string,
  savedViewId: string,
): Promise<ImportDatabaseSavedViewResult> => {
  const { success, result } = await ProductServiceAPI.deleteDatabaseSavedView(
    databaseId,
    savedViewId,
  );
  const view = success ? toImportDatabaseSavedView(result) : null;
  return {
    success: success && view !== null,
    view,
    error:
      success && view
        ? undefined
        : typeof result === 'string'
        ? result
        : 'Failed to delete saved view',
  };
};

export interface ImportPriorityIssue {
  id: string;
  databaseProductId: string;
  name: string;
  description: string;
  totalComplianceScore: number;
  issues: string[];
  normalizedIssues: ImportDatabaseProductIssue[];
}

export type ImportDatabaseIssueType =
  | 'compliance_flag'
  | 'hts_review_required'
  | 'analysis_required'
  | 'tariff_change';

export interface ImportDatabaseProductIssue {
  id: string;
  type: ImportDatabaseIssueType;
  severity: 'info' | 'warning' | 'danger';
  title: string;
  message: string;
  sourceKey: string;
  detectedAt: string | null;
  action: string;
  destination: string;
}

export interface ImportDatabaseIssueSummary {
  totalIssues: number;
  priorityIssues: ImportPriorityIssue[];
  priorityTotal: number;
  issueCounts: Partial<Record<ImportDatabaseIssueType, number>>;
  page: number;
  pageSize: number;
}

export interface ImportDatabaseIssueSummaryResult {
  success: boolean;
  summary: ImportDatabaseIssueSummary | null;
  error?: string;
}

export const fetchImportDatabaseById = async (
  databaseId: string,
): Promise<ImportServiceDatabaseResult> => {
  const { success, result } = await ProductServiceAPI.getDatabaseById(
    databaseId,
  );

  if (!success) {
    return {
      success: false,
      database: null,
      error: typeof result === 'string' ? result : 'Failed to load database',
    };
  }

  return { success: true, database: result ?? null };
};

export const fetchImportDatabaseIssueSummary = async (
  databaseId: string,
  page = 1,
  pageSize = 10,
  signal?: AbortSignal,
): Promise<ImportDatabaseIssueSummaryResult> => {
  const { success, result } = await ProductServiceAPI.getDatabaseIssueSummary(
    databaseId,
    page,
    pageSize,
    signal,
  );

  if (!success) {
    return {
      success: false,
      summary: null,
      error:
        typeof result === 'string' ? result : 'Failed to load database issues',
    };
  }

  return {
    success: true,
    summary: {
      totalIssues: Number(result?.total_issues_products ?? 0),
      priorityIssues: Array.isArray(result?.priority_issues)
        ? result.priority_issues.map((issue: any) => ({
            id: String(issue.id ?? ''),
            databaseProductId: String(issue.database_product_id ?? ''),
            name: String(issue.name ?? 'Unknown'),
            description: String(issue.description ?? ''),
            totalComplianceScore: Number(issue.total_compliance_score ?? 0),
            issues: Array.isArray(issue.issues)
              ? issue.issues
                  .filter(
                    (item: unknown): item is string => typeof item === 'string',
                  )
                  .map((item: string) => item.trim())
                  .filter(Boolean)
              : [],
            normalizedIssues: Array.isArray(issue.normalized_issues)
              ? issue.normalized_issues
                  .filter(
                    (item: unknown): item is Record<string, unknown> =>
                      Boolean(item) &&
                      typeof item === 'object' &&
                      !Array.isArray(item),
                  )
                  .map((item: Record<string, unknown>) => ({
                    id: String(item.id ?? ''),
                    type: String(
                      item.type ?? 'compliance_flag',
                    ) as ImportDatabaseIssueType,
                    severity: String(item.severity ?? 'info') as
                      | 'info'
                      | 'warning'
                      | 'danger',
                    title: String(item.title ?? 'Issue'),
                    message: String(item.message ?? ''),
                    sourceKey: String(item.source_key ?? ''),
                    detectedAt:
                      typeof item.detected_at === 'string'
                        ? item.detected_at
                        : null,
                    action: String(item.action ?? 'Review product'),
                    destination: String(item.destination ?? ''),
                  }))
              : [],
          }))
        : [],
      priorityTotal: Number(result?.priority_total ?? 0),
      issueCounts:
        result?.issue_counts &&
        typeof result.issue_counts === 'object' &&
        !Array.isArray(result.issue_counts)
          ? result.issue_counts
          : {},
      page: Number(result?.page ?? page),
      pageSize: Number(result?.page_size ?? pageSize),
    },
  };
};

export type ImportNotificationView = 'all' | 'system' | 'user';

export interface ImportDatabaseNotification {
  id: string;
  databaseId: string;
  databaseProductId: string | null;
  heading: string;
  message: string;
  type: string;
  sender: string;
  system: boolean;
  read: boolean;
  link: string | null;
  createdAt: string;
}

export interface ImportNotificationCounts {
  all: number;
  unread: number;
  system: number;
  user: number;
}

export interface ImportDatabaseNotificationsResult {
  success: boolean;
  notifications: ImportDatabaseNotification[];
  counts: ImportNotificationCounts;
  total: number;
  error?: string;
}

const emptyNotificationCounts = (): ImportNotificationCounts => ({
  all: 0,
  unread: 0,
  system: 0,
  user: 0,
});

const toImportDatabaseNotification = (
  value: any,
): ImportDatabaseNotification => ({
  id: String(value?._id ?? value?.id ?? ''),
  databaseId: String(value?.databaseId ?? ''),
  databaseProductId:
    typeof value?.databaseProductId === 'string'
      ? value.databaseProductId
      : null,
  heading: String(value?.heading ?? 'Notification'),
  message: String(value?.message ?? ''),
  type: String(value?.type ?? 'system'),
  sender: String(value?.sender ?? 'System'),
  system: value?.system === true,
  read: value?.status !== 'unread',
  link: typeof value?.link === 'string' && value.link ? value.link : null,
  createdAt: String(value?.createdAt ?? ''),
});

export const fetchImportDatabaseNotifications = async (
  databaseId: string,
  view: ImportNotificationView = 'all',
  unreadOnly = false,
  limit = 25,
  offset = 0,
  countsOnly = false,
  signal?: AbortSignal,
): Promise<ImportDatabaseNotificationsResult> => {
  const { success, result } = await ProductServiceAPI.getDatabaseNotifications(
    databaseId,
    {
      view,
      unreadOnly,
      limit,
      offset,
      countsOnly,
    },
    signal,
  );

  if (!success) {
    return {
      success: false,
      notifications: [],
      counts: emptyNotificationCounts(),
      total: 0,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load database notifications',
    };
  }

  const counts = result?.counts ?? {};
  return {
    success: true,
    notifications: Array.isArray(result?.notifications)
      ? result.notifications
          .map(toImportDatabaseNotification)
          .filter((notification: ImportDatabaseNotification) =>
            Boolean(notification.id),
          )
      : [],
    counts: {
      all: Number(counts.all ?? 0),
      unread: Number(counts.unread ?? 0),
      system: Number(counts.system ?? 0),
      user: Number(counts.user ?? 0),
    },
    total: Number(result?.pagination?.total ?? 0),
  };
};

export const fetchImportGroupNotifications = async (
  groupId: string,
  view: ImportNotificationView = 'all',
  unreadOnly = false,
  limit = 25,
  offset = 0,
  countsOnly = false,
  signal?: AbortSignal,
): Promise<ImportDatabaseNotificationsResult> => {
  const { success, result } = await ProductServiceAPI.getGroupNotifications(
    groupId,
    {
      view,
      unreadOnly,
      limit,
      offset,
      countsOnly,
    },
    signal,
  );

  if (!success) {
    return {
      success: false,
      notifications: [],
      counts: emptyNotificationCounts(),
      total: 0,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load team notifications',
    };
  }

  const counts = result?.counts ?? {};
  return {
    success: true,
    notifications: Array.isArray(result?.notifications)
      ? result.notifications
          .map(toImportDatabaseNotification)
          .filter((notification: ImportDatabaseNotification) =>
            Boolean(notification.id),
          )
      : [],
    counts: {
      all: Number(counts.all ?? 0),
      unread: Number(counts.unread ?? 0),
      system: Number(counts.system ?? 0),
      user: Number(counts.user ?? 0),
    },
    total: Number(result?.pagination?.total ?? 0),
  };
};

export const markImportDatabaseNotificationRead = async (
  databaseId: string,
  notificationId: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } =
    await ProductServiceAPI.markDatabaseNotificationRead(
      databaseId,
      notificationId,
    );

  return success
    ? { success: true }
    : {
        success: false,
        error:
          typeof result === 'string'
            ? result
            : 'Failed to mark notification as read',
      };
};

export const markAllImportDatabaseNotificationsRead = async (
  databaseId: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } =
    await ProductServiceAPI.markAllDatabaseNotificationsRead(databaseId);

  return success
    ? { success: true }
    : {
        success: false,
        error:
          typeof result === 'string'
            ? result
            : 'Failed to mark all notifications as read',
      };
};

export const markAllImportGroupNotificationsRead = async (
  groupId: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } =
    await ProductServiceAPI.markAllGroupNotificationsRead(groupId);

  return success
    ? { success: true }
    : {
        success: false,
        error:
          typeof result === 'string'
            ? result
            : 'Failed to mark all notifications as read',
      };
};

export const dismissImportDatabaseNotification = async (
  databaseId: string,
  notificationId: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } =
    await ProductServiceAPI.dismissDatabaseNotification(
      databaseId,
      notificationId,
    );

  return success
    ? { success: true }
    : {
        success: false,
        error:
          typeof result === 'string'
            ? result
            : 'Failed to dismiss notification',
      };
};

export const bulkDeleteImportDatabaseProducts = async (
  databaseId: string,
  databaseProductIds: string[],
): Promise<{ success: boolean; deleted: number; error?: string }> => {
  const { success, result } =
    await ProductServiceAPI.bulkDeleteDatabaseProducts(
      databaseId,
      databaseProductIds,
    );
  return success
    ? { success: true, deleted: Number(result?.deleted ?? 0) }
    : {
        success: false,
        deleted: 0,
        error:
          typeof result === 'string'
            ? result
            : 'Failed to remove selected products',
      };
};

export interface ImportBulkTariffExclusionOption {
  code: string;
  codePretty: string;
  label: string;
  note?: string;
  rateDisplay?: string;
  question?: string;
  applicableCount: number;
  appliedCount: number;
  applyUpdateCount: number;
}

export interface ImportBulkTariffExclusionOptionsResult {
  success: boolean;
  selectedCount: number;
  matchedCount: number;
  screenedCount: number;
  options: ImportBulkTariffExclusionOption[];
  error?: string;
}

export interface ImportBulkTariffExclusionUpdateResult {
  success: boolean;
  requested: number;
  matched: number;
  eligible: number;
  updated: number;
  unchanged: number;
  inapplicable: number;
  failed: number;
  error?: string;
}

export const fetchBulkImportTariffExclusionOptions = async (
  databaseId: string,
  databaseProductIds: string[],
): Promise<ImportBulkTariffExclusionOptionsResult> => {
  const { success, result } =
    await ProductServiceAPI.getBulkTariffExclusionOptions(
      databaseId,
      databaseProductIds,
    );
  if (!success) {
    return {
      success: false,
      selectedCount: databaseProductIds.length,
      matchedCount: 0,
      screenedCount: 0,
      options: [],
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load applicable tariff exclusions',
    };
  }

  return {
    success: true,
    selectedCount: Number(result?.selected_count ?? databaseProductIds.length),
    matchedCount: Number(result?.matched_count ?? 0),
    screenedCount: Number(result?.screened_count ?? 0),
    options: Array.isArray(result?.options)
      ? result.options.map((option: any) => ({
          code: String(option?.code ?? ''),
          codePretty: String(option?.code_pretty ?? option?.code ?? ''),
          label: String(option?.label ?? ''),
          note:
            typeof option?.note === 'string' && option.note.trim()
              ? option.note.trim()
              : undefined,
          rateDisplay:
            typeof option?.rate_display === 'string' &&
            option.rate_display.trim()
              ? option.rate_display.trim()
              : undefined,
          question:
            typeof option?.question === 'string' && option.question.trim()
              ? option.question.trim()
              : undefined,
          applicableCount: Number(option?.applicable_count ?? 0),
          appliedCount: Number(option?.applied_count ?? 0),
          applyUpdateCount: Number(option?.apply_update_count ?? 0),
        }))
      : [],
  };
};

export const bulkUpdateImportTariffExclusion = async (
  databaseId: string,
  databaseProductIds: string[],
  exclusionCode: string,
  action: 'apply' | 'remove',
): Promise<ImportBulkTariffExclusionUpdateResult> => {
  const { success, result } = await ProductServiceAPI.bulkUpdateTariffExclusion(
    databaseId,
    databaseProductIds,
    exclusionCode,
    action,
  );
  if (!success) {
    return {
      success: false,
      requested: databaseProductIds.length,
      matched: 0,
      eligible: 0,
      updated: 0,
      unchanged: 0,
      inapplicable: 0,
      failed: databaseProductIds.length,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to update tariff exclusions',
    };
  }

  return {
    success: true,
    requested: Number(result?.requested ?? databaseProductIds.length),
    matched: Number(result?.matched ?? 0),
    eligible: Number(result?.eligible ?? 0),
    updated: Number(result?.updated ?? 0),
    unchanged: Number(result?.unchanged ?? 0),
    inapplicable: Number(result?.inapplicable ?? 0),
    failed: Number(result?.failed ?? 0),
  };
};

/**
 * Shape returned by GET /product/database-id/:databaseId (biteservices
 * product service — see product.service.ts#getProductsByDatabaseIdNoLimit)
 * and by GET /product/:id (see product.service.ts#getProductById, which
 * returns the same row plus a few detail-only fields). Limited to the
 * columns the adapters below actually read; only `id`/`name` are guaranteed.
 * The row carries several other real columns (screening, hit counts, ...)
 * that aren't modeled here since nothing consumes them yet.
 */
export type ImportProductSource =
  | 'document_upload'
  | 'xml'
  | 'csv_xlsx'
  | 'manual'
  | 'unknown';

export interface ImportServiceProduct {
  id: string;
  name: string;
  description?: string | null;
  traces?: string | null;
  hts_code?: string | null;
  country_of_origin?: string | null;
  model_number?: string | null;
  unit_of_measure?: string | null;
  product_value?: number | null;
  compliance_score?: number | null;
  database_id?: string | null;
  category?: string | null;
  hierarchy_type?: string | null;
  child_products?: number | string | null;
  database_structure?: string | null;
  relationship_id?: string | null;
  relationship_quantity?: number | null;
  relationship_unit_of_measure?: string | null;
  relationship_unit_value?: number | null;
  parent_product_id?: string | null;
  parent_database_product_id?: string | null;
  parent_name?: string | null;
  parent_model_number?: string | null;
  parent_hierarchy_type?: string | null;
  manufacturer?: string | null;
  vendor?: string | null;
  specifications?: string | null;
  quantity?: number | null;
  copper_value?: number | null;
  copper_percent?: number | null;
  aluminum_value?: number | null;
  aluminum_percent?: number | null;
  steel_value?: number | null;
  steel_percent?: number | null;
  country_of_smelt_pour?: string | null;
  entry_date?: string | null;
  loading_date?: string | null;
  trans_method?: string | null;
  material_pct?: number | null;
  material_weight?: number | null;
  additional_columns?: any;
  // Internal shape ({id, product_id, country_of_origin, hts_code, ...}) isn't consumed here.
  additional_hts_codes?: any[];
  cross_rulings?: string[];
  issues?: string[];
  clear_flags?: string[];
  // Latest product-service tariff-calculator snapshot (base/results/math).
  tariff_result?: any;
  has_tariff_result?: boolean | null;
  // False when the stored result is "Tariff calculation unavailable" (no math).
  has_tariff_calculation?: boolean | null;
  base_duty_rate?: string | number | null;
  additional_duty_rate?: string | number | null;
  total_cost?: string | number | null;
  duty_cost?: string | number | null;
  additional_duty_cost?: string | number | null;
  section_301?: boolean | null;
  section_232?: boolean | null;
  section_301_codes?: string | null;
  section_301_rate?: string | number | null;
  section_301_value?: string | number | null;
  section_232_codes?: string | null;
  section_232_rate?: string | number | null;
  section_232_value?: string | number | null;
  raw_section_301_duty_cost?: string | number | null;
  raw_section_232_duty_cost?: string | number | null;
  fta_options?: unknown[] | null;
  fta_evaluated?: boolean | null;
  hts_invalid?: boolean | null;
  hts_requires_attention?: boolean | null;
  tariff_diff?: any;
  tariff_screen_date?: string | null;
  tariff_saved_at?: string | null;
  all_results?: Array<Record<string, unknown>> | null;
  pga_agencies?: string[] | null;
  // PGA program codes; null when screened before program codes were stored.
  pga_programs?: string[] | null;
  // Null when screened before these were stored.
  pga_flag_codes?: string[] | null;
  adcvd_cases?: string[] | null;
  has_pga_codes?: boolean | null;
  has_ad_cvd_hits?: boolean | null;
  ai_summary?: Record<string, unknown> | null;
  risk_level?: string | null;
  database_product_id?: string | null;
  import_source?: ImportProductSource | null;
  database_name?: string | null;
  updated_at?: string | null;
  product_screening_created_at?: string | null;
  last_screened_at?: string | null;
}

export interface ImportProductCategory {
  id?: string;
  name: string;
  group_id?: string | null;
}

export interface ImportHtsOption {
  code: string;
  label: string;
  description: string;
}

export type ImportProductDetailsUpdate = Partial<{
  name: string;
  description: string | null;
  traces: string | null;
  specifications: string | null;
  category: string | null;
  hierarchy_type: string | null;
  parent_product_id: string | null;
  manufacturer: string | null;
  vendor: string | null;
  model_number: string | null;
  country_of_origin: string | null;
  hts_code: string;
  country_of_smelt_pour: string | null;
  trans_method: string | null;
  entry_date: string | null;
  loading_date: string | null;
  unit_of_measure: string | null;
  quantity: number | null;
  additional_columns: Record<string, string>;
  additional_hts_codes: Array<{
    id?: string;
    hts_code: string;
    country_of_origin: string | null;
  }>;
  product_value: number;
  copper_percent: number;
  copper_value: number;
  aluminum_percent: number;
  aluminum_value: number;
  steel_percent: number;
  steel_value: number;
  expected_updated_at?: string;
  enabledExclusions?: Record<string, boolean>;
  enabledFtas?: Record<string, boolean>;
}>;

export interface ImportProductDetailsSaveResult {
  success: boolean;
  product: ImportServiceProduct | null;
  tariffResult?: any;
  explanationContext?: ImportTariffExplanationContext | null;
  recalculated: boolean;
  recalculationError?: string;
  error?: string;
}

export const fetchImportProductCategories = async (
  groupId: string,
): Promise<{
  success: boolean;
  categories: ImportProductCategory[];
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getProductCategories(
    groupId,
  );
  if (!success) {
    return {
      success: false,
      categories: [],
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load product categories',
    };
  }
  return {
    success: true,
    categories: Array.isArray(result) ? result : [],
  };
};

export const createImportProductCategory = async (
  groupId: string,
  name: string,
): Promise<{
  success: boolean;
  category: ImportProductCategory | null;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.createProductCategory(
    groupId,
    name,
  );
  if (!success) {
    return {
      success: false,
      category: null,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to create product category',
    };
  }
  return {
    success: true,
    category:
      result && typeof result === 'object'
        ? (result as ImportProductCategory)
        : null,
  };
};

export const fetchImportHtsOptions = async (
  prefix: string,
  country?: string,
): Promise<{
  success: boolean;
  options: ImportHtsOption[];
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.searchHtsCodes({
    prefix,
    country,
  });
  if (!success) {
    return {
      success: false,
      options: [],
      error: typeof result === 'string' ? result : 'Failed to load HTS codes',
    };
  }
  return {
    success: true,
    options: Array.isArray(result?.options) ? result.options : [],
  };
};

export const saveImportProductDetails = async (
  databaseProductId: string,
  update: ImportProductDetailsUpdate,
): Promise<ImportProductDetailsSaveResult> => {
  const { success, result } = await ProductServiceAPI.updateProductDetails(
    databaseProductId,
    update,
  );
  if (!success) {
    return {
      success: false,
      product: null,
      tariffResult: undefined,
      explanationContext: null,
      recalculated: false,
      error:
        typeof result === 'string' ? result : 'Failed to save product details',
    };
  }
  return {
    success: true,
    product: result?.product ?? null,
    tariffResult: result?.tariffResult,
    explanationContext: result?.explanationContext ?? null,
    recalculated: result?.recalculated === true,
    recalculationError:
      typeof result?.recalculationError === 'string'
        ? result.recalculationError
        : undefined,
  };
};

export interface ImportBulkDetailsResult {
  success: boolean;
  updated: number;
  failed: number;
  error?: string;
}

// No batch "update product details" endpoint exists — this fans out over the
// single-product PATCH with a small concurrency cap so a page-sized selection
// doesn't fire dozens of requests at once. `update` is applied identically to
// every id, so `expected_updated_at` (the detail page's optimistic-concurrency
// guard) is deliberately omitted: a bulk edit is last-write-wins by design.
export const bulkUpdateImportProductDetails = async (
  databaseProductIds: string[],
  update: ImportProductDetailsUpdate,
  onProgress?: (done: number, total: number) => void,
): Promise<ImportBulkDetailsResult> => {
  const total = databaseProductIds.length;
  let updated = 0;
  let failed = 0;
  let done = 0;
  let cursor = 0;
  const concurrency = Math.min(5, total);

  const worker = async () => {
    while (cursor < total) {
      const index = cursor;
      cursor += 1;
      const { success } = await saveImportProductDetails(
        databaseProductIds[index],
        update,
      );
      if (success) {
        updated += 1;
      } else {
        failed += 1;
      }
      done += 1;
      onProgress?.(done, total);
    }
  };

  await Promise.all(Array.from({ length: concurrency }, worker));

  return {
    success: failed === 0,
    updated,
    failed,
    error: failed > 0 ? 'One or more products failed to update.' : undefined,
  };
};

export interface ImportServiceProductsResult {
  success: boolean;
  products: ImportServiceProduct[];
  total: number;
  limit: number;
  offset: number;
  facets: {
    origins: string[];
    databases: Array<{ id: string; name: string }>;
  };
  facetsIncluded: boolean;
  error?: string;
}

export interface ImportProductListQuery {
  limit?: number;
  offset?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  risk?: string[];
  pga?: string[];
  tariff?: string[];
  valueBuckets?: string[];
  fta?: string[];
  origins?: string[];
  databaseIds?: string[];
  additionalDutyBuckets?: string[];
  scoreBuckets?: string[];
  classification?: string[];
  issueTypes?: string[];
  hierarchyTypes?: string[];
  // Explicit Product ids (at most 500 per request).
  productIds?: string[];
  includeFacets?: boolean;
}

export interface ImportProductListSummary {
  total: number;
  assessed: number;
  averageComplianceScore: number | null;
  highRisk: number;
  flagged: number;
  totalIssues: number;
  totalValue: number;
  totalDuty: number;
  riskDistribution: Record<string, number>;
  pgaBreakdown: Record<string, number>;
  tariffExposure: {
    section301: number;
    section232: number;
    otherAdditional: number;
  };
  countryCounts: Record<string, number>;
}

export interface ImportProductListSummaryResult {
  success: boolean;
  summary: ImportProductListSummary | null;
  error?: string;
}

const toProductListSummaryResult = (
  success: boolean,
  result: any,
): ImportProductListSummaryResult => {
  if (!success) {
    return {
      success: false,
      summary: null,
      error:
        typeof result === 'string' ? result : 'Failed to load product summary',
    };
  }
  return {
    success: true,
    summary: {
      total: Number(result?.total ?? 0),
      assessed: Number(result?.assessed ?? 0),
      averageComplianceScore:
        result?.averageComplianceScore === null ||
        result?.averageComplianceScore === undefined
          ? null
          : Number(result.averageComplianceScore),
      highRisk: Number(result?.highRisk ?? 0),
      flagged: Number(result?.flagged ?? 0),
      totalIssues: Number(result?.totalIssues ?? 0),
      totalValue: Number(result?.totalValue ?? 0),
      totalDuty: Number(result?.totalDuty ?? 0),
      riskDistribution: result?.riskDistribution ?? {},
      pgaBreakdown: result?.pgaBreakdown ?? {},
      tariffExposure: {
        section301: Number(result?.tariffExposure?.section301 ?? 0),
        section232: Number(result?.tariffExposure?.section232 ?? 0),
        otherAdditional: Number(result?.tariffExposure?.otherAdditional ?? 0),
      },
      countryCounts: result?.countryCounts ?? {},
    },
  };
};

export const fetchImportProductsSummaryByGroupId = async (
  groupId: string,
  signal?: AbortSignal,
): Promise<ImportProductListSummaryResult> => {
  const { success, result } =
    await ProductServiceAPI.getProductsSummaryByGroupId(groupId, signal);
  return toProductListSummaryResult(success, result);
};

export interface ImportTariffExposureSlice {
  products: number;
  duty: number;
}

// Database-wide duty split into base duty, each tariff program, and additional
// duty no program accounts for; the three sum to totalDuty.
export interface ImportDatabaseTariffExposure {
  baseDuty: ImportTariffExposureSlice;
  programs: Array<
    ImportTariffExposureSlice & { bucket: string; label: string }
  >;
  otherAdditional: ImportTariffExposureSlice;
  totalDuty: number;
  // Keyed by database product id; present only when requested.
  products?: Record<
    string,
    {
      baseDuty: number;
      programs: Record<string, number>;
      otherAdditional: number;
    }
  >;
}

// Products per PGA program (agency), from each product's latest screening hits.
export interface ImportDatabasePgaExposure {
  programs: Array<{ code: string; label: string; products: number }>;
  productsWithHits: number;
}

export const fetchImportDatabasePgaExposure = async (
  databaseId: string,
  signal?: AbortSignal,
): Promise<{
  success: boolean;
  exposure?: ImportDatabasePgaExposure;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getDatabasePgaExposure(
    databaseId,
    signal,
  );
  if (!success || !result || typeof result !== 'object') {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to load PGA exposure',
    };
  }
  return { success: true, exposure: result as ImportDatabasePgaExposure };
};

export const fetchImportDatabaseTariffExposure = async (
  databaseId: string,
  options: {
    includeProducts?: boolean;
    // Limit the exposure to these database products instead of the whole
    // database.
    databaseProductIds?: string[];
    signal?: AbortSignal;
  } = {},
): Promise<{
  success: boolean;
  exposure?: ImportDatabaseTariffExposure;
  error?: string;
}> => {
  const { success, result } = options.databaseProductIds
    ? await ProductServiceAPI.getDatabaseTariffExposureForProducts(
        databaseId,
        options.databaseProductIds,
        options.includeProducts ?? false,
        options.signal,
      )
    : await ProductServiceAPI.getDatabaseTariffExposure(
        databaseId,
        options.includeProducts ?? false,
        options.signal,
      );
  if (!success || !result || typeof result !== 'object') {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to load tariff exposure',
    };
  }
  return { success: true, exposure: result as ImportDatabaseTariffExposure };
};

export const fetchImportProductsSummaryByDatabaseId = async (
  databaseId: string,
  signal?: AbortSignal,
): Promise<ImportProductListSummaryResult> => {
  const { success, result } =
    await ProductServiceAPI.getProductsSummaryByDatabaseId(databaseId, signal);
  return toProductListSummaryResult(success, result);
};

const productListParams = (
  query: ImportProductListQuery,
): Record<string, string | number | boolean | undefined> => {
  const params: Record<string, string | number | boolean | undefined> = {
    paginated: true,
    limit: query.limit ?? 25,
    offset: query.offset ?? 0,
    search: query.search,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
    includeFacets: query.includeFacets,
    hierarchy_type: query.hierarchyTypes?.length
      ? query.hierarchyTypes.join(',')
      : undefined,
  };
  const lists: Array<[keyof ImportProductListQuery, string]> = [
    ['risk', 'risk'],
    ['pga', 'pga'],
    ['tariff', 'tariff'],
    ['valueBuckets', 'valueBuckets'],
    ['fta', 'fta'],
    ['origins', 'origins'],
    ['databaseIds', 'databaseIds'],
    ['additionalDutyBuckets', 'additionalDutyBuckets'],
    ['scoreBuckets', 'scoreBuckets'],
    ['classification', 'classification'],
    ['issueTypes', 'issueTypes'],
    ['productIds', 'productIds'],
  ];
  lists.forEach(([source, target]) => {
    const value = query[source];
    if (Array.isArray(value) && value.length) params[target] = value.join(',');
  });
  return params;
};

const toImportServiceProductsResult = (
  success: boolean,
  result: any,
  query: ImportProductListQuery,
): ImportServiceProductsResult => {
  if (!success) {
    return {
      success: false,
      products: [],
      total: 0,
      limit: query.limit ?? 25,
      offset: query.offset ?? 0,
      facets: { origins: [], databases: [] },
      facetsIncluded: false,
      error: typeof result === 'string' ? result : 'Failed to load products',
    };
  }
  return {
    success: true,
    products: Array.isArray(result?.products) ? result.products : [],
    total: Number(result?.total ?? 0),
    limit: Number(result?.limit ?? query.limit ?? 25),
    offset: Number(result?.offset ?? query.offset ?? 0),
    facets: {
      origins: Array.isArray(result?.facets?.origins)
        ? result.facets.origins
        : [],
      databases: Array.isArray(result?.facets?.databases)
        ? result.facets.databases
        : [],
    },
    facetsIncluded: result?.facets !== undefined,
  };
};

export const fetchImportProductsByGroupId = async (
  groupId: string,
  query: ImportProductListQuery = {},
  signal?: AbortSignal,
): Promise<ImportServiceProductsResult> => {
  const { success, result } = await ProductServiceAPI.getProductsByGroupId(
    groupId,
    productListParams(query),
    signal,
  );
  return toImportServiceProductsResult(success, result, query);
};

export const fetchImportProductsByDatabaseId = async (
  databaseId: string,
  query: ImportProductListQuery = {},
  signal?: AbortSignal,
): Promise<ImportServiceProductsResult> => {
  const { success, result } = await ProductServiceAPI.getProductsByDatabaseId(
    databaseId,
    productListParams(query),
    signal,
  );
  return toImportServiceProductsResult(success, result, query);
};

export const IMPORT_HIERARCHY_PARENT_TYPES: Record<string, string[]> = {
  'Sub-Assembly': ['Assembly'],
  Component: ['Assembly', 'Sub-Assembly'],
};

export interface ImportParentCandidate {
  id: string;
  name: string;
  sku: string;
  hierarchyType: string;
}

export const searchImportParentCandidates = async (
  databaseId: string,
  childHierarchyType: string,
  search: string,
  signal?: AbortSignal,
): Promise<{
  success: boolean;
  candidates: ImportParentCandidate[];
  error?: string;
}> => {
  const hierarchyTypes = IMPORT_HIERARCHY_PARENT_TYPES[childHierarchyType];
  if (!hierarchyTypes) return { success: true, candidates: [] };
  const { success, products, error } = await fetchImportProductsByDatabaseId(
    databaseId,
    {
      limit: 20,
      search: search.trim() || undefined,
      sortBy: 'name',
      sortOrder: 'asc',
      hierarchyTypes,
      includeFacets: false,
    },
    signal,
  );
  return {
    success,
    error,
    candidates: products.map((product) => ({
      id: product.id,
      name: product.name,
      sku: product.model_number ?? '',
      hierarchyType: product.hierarchy_type ?? '',
    })),
  };
};

export interface ImportServiceProductResult {
  success: boolean;
  product: ImportServiceProduct | null;
  error?: string;
}

export const fetchImportProductById = async (
  productId: string,
): Promise<ImportServiceProductResult> => {
  const { success, result } = await ProductServiceAPI.getProductById(productId);

  if (!success) {
    return {
      success: false,
      product: null,
      error: typeof result === 'string' ? result : 'Failed to load product',
    };
  }

  return { success: true, product: result ?? null };
};

/**
 * Adapts a product-service row to the table model. Screening-derived PGA/risk
 * fields and stored tariff-calculator fields are mapped here so table filters,
 * sorting, and displayed values all use the same live source.
 */
// Real Base Duty / Add'l Tariff / HTS-validity for the products table —
// mirrors the legacy ProductRow.tsx's reading of the same tariff_result blob.
// The math block is authoritative for aggregate additional duty; duty rows
// supply Section 301/232 classification, while status supplies HTS validity.
// Returns {} when the product has no tariff result.
const PRODUCT_PGA_AGENCIES: PgaAgency[] = [
  'FDA',
  'EPA',
  'USDA',
  'FWS',
  'CPSC',
  'TSCA',
  'DOT',
];
const PRODUCT_PGA_AGENCY_ALIASES: Record<PgaAgency, string[]> = {
  FDA: ['FDA', 'FOOD AND DRUG'],
  EPA: ['EPA', 'ENVIRONMENTAL PROTECTION'],
  USDA: ['USDA', 'AGRICULTURE', 'APHIS', 'FSIS'],
  FWS: ['FWS', 'FISH AND WILDLIFE'],
  CPSC: ['CPSC', 'CONSUMER PRODUCT SAFETY'],
  TSCA: ['TSCA', 'TOXIC SUBSTANCES CONTROL'],
  DOT: ['DOT', 'DEPARTMENT OF TRANSPORTATION'],
};
const PRODUCT_PGA_CODE_AGENCIES: Array<{
  prefix: string;
  agencies: PgaAgency[];
}> = [
  { prefix: 'EPA_TSCA', agencies: ['EPA', 'TSCA'] },
  { prefix: 'EPA_', agencies: ['EPA'] },
  { prefix: 'FDA', agencies: ['FDA'] },
  { prefix: 'APH_', agencies: ['USDA'] },
  { prefix: 'AMS', agencies: ['USDA'] },
  { prefix: 'FSIS', agencies: ['USDA'] },
  { prefix: 'FWS', agencies: ['FWS'] },
  { prefix: 'CPS', agencies: ['CPSC'] },
  { prefix: 'DTC', agencies: ['DOT'] },
  { prefix: 'NHT', agencies: ['DOT'] },
];

const parsePercentValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const match = value.match(/-?\d+(?:\.\d+)?/);
  if (!match) return undefined;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getTariffRows = (tariffResult: any): any[] =>
  Array.isArray(tariffResult?.results?.lines)
    ? tariffResult.results.lines.flatMap((line: any) =>
        Array.isArray(line?.rows) ? line.rows : [],
      )
    : [];

const tariffRowText = (row: any): string =>
  `${row?.type ?? ''} ${row?.label ?? ''}`.toLowerCase();

const tariffRowCode = (row: any): string =>
  String(row?._codeDigits ?? row?.code ?? '').replace(/\D/g, '');

const isSection301Row = (row: any): boolean => {
  const text = tariffRowText(row);
  const code = tariffRowCode(row);
  return (
    text.includes('section 301') ||
    text.includes('sec 301') ||
    code.startsWith('990388') ||
    code.startsWith('990389')
  );
};

const isSection232Row = (row: any): boolean => {
  const text = tariffRowText(row);
  const code = tariffRowCode(row);
  return (
    !text.includes('quota') &&
    (text.includes('section 232') ||
      text.includes('sec 232') ||
      code.startsWith('990380'))
  );
};

const roundCurrency = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const tariffDutyForRows = (
  tariffResult: any,
  productValue: number,
  matches: (row: any) => boolean,
): number => {
  const lines = Array.isArray(tariffResult?.results?.lines)
    ? tariffResult.results.lines
    : [];
  return roundCurrency(
    lines.reduce((total: number, line: any) => {
      const lineValue = toFiniteNumber(line?.value) ?? productValue;
      const rows = Array.isArray(line?.rows) ? line.rows : [];
      const seen = new Set<string>();
      const lineDuty = rows.reduce((sum: number, row: any) => {
        if (!matches(row)) return sum;
        const key = `${tariffRowCode(row)}|${row?.type ?? ''}|${
          row?.rate ?? ''
        }`;
        if (seen.has(key)) return sum;
        seen.add(key);
        return sum + lineValue * (parseRatePct(row?.rate) / 100);
      }, 0);
      return total + lineDuty;
    }, 0),
  );
};

const uniqueRateTotal = (rows: any[]): number => {
  const seen = new Set<string>();
  return rows.reduce((total, row) => {
    const key = `${row?.code ?? ''}|${row?.type ?? ''}|${row?.rate ?? ''}`;
    if (seen.has(key)) return total;
    seen.add(key);
    return total + parseRatePct(row?.rate);
  }, 0);
};

const deriveAvailableFtaPrograms = (tariffResult: any): string[] => {
  const options = tariffResult?.results?.ftaOptions;
  if (!Array.isArray(options)) return [];

  return Array.from(
    new Set(
      options
        .map((option: unknown) => {
          if (typeof option === 'string') return option.trim();
          if (!option || typeof option !== 'object') return '';
          const candidate = option as { code?: unknown; name?: unknown };
          const name =
            typeof candidate.name === 'string' ? candidate.name.trim() : '';
          const code =
            typeof candidate.code === 'string' ? candidate.code.trim() : '';
          return name || code;
        })
        .filter(Boolean),
    ),
  );
};

function deriveTariffDisplayFields(
  tariffResult: any,
  productValue?: number | null,
): {
  baseDutyRate?: string;
  additionalTariffDisplay?: string;
  additionalDutyPct?: number;
  section301?: boolean;
  section301Rate?: number;
  section232?: boolean;
  section232Rate?: number;
  ftaEligible?: boolean;
  ftaEvaluated?: boolean;
  ftaPrograms?: string[];
  htsInvalid?: boolean;
  dutyCost?: number;
  additionalDutyCost?: number;
  section301DutyCost?: number;
  section232DutyCost?: number;
  otherAdditionalDutyCost?: number;
} {
  if (!tariffResult) return {};

  const rawBaseDutyRate = tariffResult?.base?.baseDutyRate;
  const baseDutyRate =
    typeof rawBaseDutyRate === 'string'
      ? rawBaseDutyRate
      : typeof rawBaseDutyRate === 'number'
      ? `${rawBaseDutyRate}%`
      : undefined;
  const rows = getTariffRows(tariffResult);
  const section301Rows = rows.filter(isSection301Row);
  const section232Rows = rows.filter(isSection232Row);
  const nonBaseRows = rows.filter(
    (row) => !tariffRowText(row).startsWith('base duty'),
  );
  const math = tariffResult?.math;
  const additionalDutyPct =
    parsePercentValue(math?.additionalDutyRateDisplay) ??
    parsePercentValue(math?.additionalDutyRate) ??
    (toFiniteNumber(math?.additionalDutyDollars) !== undefined &&
    toFiniteNumber(productValue) !== undefined &&
    (toFiniteNumber(productValue) ?? 0) > 0
      ? ((toFiniteNumber(math?.additionalDutyDollars) ?? 0) /
          (toFiniteNumber(productValue) ?? 1)) *
        100
      : uniqueRateTotal(nonBaseRows));
  const additionalTariffDisplay = `${additionalDutyPct.toFixed(2)}%`;
  // ftaOptions describes programs the product qualifies for. enabledFtas is
  // intentionally only the user's applied-selection state and must not drive
  // eligibility or the Eligible/Ineligible product filters.
  const ftaPrograms = deriveAvailableFtaPrograms(tariffResult);
  // A tariff result that never carried ftaOptions means FTA eligibility was not
  // evaluated — it is not evidence of ineligibility. Claiming evaluated:true
  // here would put those products in the Ineligible filter bucket.
  const ftaEvaluated = Array.isArray(tariffResult?.results?.ftaOptions);

  const savedHtsClassification = tariffResult?.insights?.htsClassification;
  const htsInvalid =
    savedHtsClassification?.valid === false ||
    tariffResult?.status?.message === 'HTS Document Not Found (unverified)';
  const normalizedProductValue = toFiniteNumber(productValue) ?? 0;
  const dutyCost = toFiniteNumber(math?.dutyCost) ?? 0;
  const additionalDutyCost = toFiniteNumber(math?.additionalDutyDollars) ?? 0;
  const rawSection301DutyCost = tariffDutyForRows(
    tariffResult,
    normalizedProductValue,
    isSection301Row,
  );
  const rawSection232DutyCost = tariffDutyForRows(
    tariffResult,
    normalizedProductValue,
    isSection232Row,
  );
  const classifiedAdditionalCost =
    rawSection301DutyCost + rawSection232DutyCost;
  const classificationScale =
    classifiedAdditionalCost > additionalDutyCost &&
    classifiedAdditionalCost > 0
      ? additionalDutyCost / classifiedAdditionalCost
      : 1;
  const section301DutyCost = roundCurrency(
    rawSection301DutyCost * classificationScale,
  );
  const section232DutyCost = roundCurrency(
    rawSection232DutyCost * classificationScale,
  );
  const otherAdditionalDutyCost = roundCurrency(
    Math.max(0, additionalDutyCost - section301DutyCost - section232DutyCost),
  );

  return {
    baseDutyRate,
    additionalTariffDisplay,
    additionalDutyPct,
    section301: section301Rows.length > 0,
    section301Rate: uniqueRateTotal(section301Rows),
    section232: section232Rows.length > 0,
    section232Rate: uniqueRateTotal(section232Rows),
    ftaEligible: ftaPrograms.length > 0,
    ftaEvaluated,
    ftaPrograms,
    htsInvalid,
    dutyCost,
    additionalDutyCost,
    section301DutyCost,
    section232DutyCost,
    otherAdditionalDutyCost,
  };
}

// Paginated product endpoints deliberately return compact scalar tariff
// fields instead of the complete ProductTariffResult JSON. This adapter keeps
// the table model identical while preventing large tariff line/rule payloads
// from being downloaded for every visible row.
const deriveCompactTariffDisplayFields = (
  row: ImportServiceProduct,
): ReturnType<typeof deriveTariffDisplayFields> & { totalCost?: number } => {
  if (row.has_tariff_result !== true) return {};

  const declaredValue = toFiniteNumber(row.product_value) ?? 0;
  const additionalDutyPct = toFiniteNumber(row.additional_duty_rate) ?? 0;
  const additionalDutyCost = toFiniteNumber(row.additional_duty_cost) ?? 0;
  const rawSection301 = toFiniteNumber(row.raw_section_301_duty_cost) ?? 0;
  const rawSection232 = toFiniteNumber(row.raw_section_232_duty_cost) ?? 0;
  const classifiedAdditionalCost = rawSection301 + rawSection232;
  const classificationScale =
    classifiedAdditionalCost > additionalDutyCost &&
    classifiedAdditionalCost > 0
      ? additionalDutyCost / classifiedAdditionalCost
      : 1;
  const section301DutyCost = roundCurrency(rawSection301 * classificationScale);
  const section232DutyCost = roundCurrency(rawSection232 * classificationScale);
  const ftaPrograms = deriveAvailableFtaPrograms({
    results: { ftaOptions: row.fta_options },
  });
  const baseDutyRate =
    typeof row.base_duty_rate === 'number'
      ? `${row.base_duty_rate}%`
      : typeof row.base_duty_rate === 'string'
      ? row.base_duty_rate
      : undefined;

  return {
    baseDutyRate,
    additionalTariffDisplay: `${additionalDutyPct.toFixed(2)}%`,
    additionalDutyPct,
    section301: row.section_301 === true,
    section301Rate:
      declaredValue > 0 ? (rawSection301 / declaredValue) * 100 : 0,
    section232: row.section_232 === true,
    section232Rate:
      declaredValue > 0 ? (rawSection232 / declaredValue) * 100 : 0,
    ftaEligible: ftaPrograms.length > 0,
    ftaEvaluated: row.fta_evaluated === true,
    ftaPrograms,
    htsInvalid: row.hts_invalid === true,
    totalCost: toFiniteNumber(row.total_cost),
    dutyCost: toFiniteNumber(row.duty_cost) ?? 0,
    additionalDutyCost,
    section301DutyCost,
    section232DutyCost,
    otherAdditionalDutyCost: roundCurrency(
      Math.max(0, additionalDutyCost - section301DutyCost - section232DutyCost),
    ),
  };
};

const deriveRiskLevel = (row: ImportServiceProduct): ImportRisk => {
  const raw =
    row.risk_level ??
    row.ai_summary?.risk_level ??
    row.ai_summary?.riskLevel ??
    row.ai_summary?.risk_classification;
  const normalized = String(raw ?? '').toLowerCase();
  if (normalized === 'critical') return 'Critical';
  if (normalized === 'high') return 'High';
  if (normalized === 'medium') return 'Medium';
  if (normalized === 'low') return 'Low';
  if (normalized === 'no matches') return 'No Matches';
  return 'Not Assessed';
};

export const derivePgaFlags = (row: ImportServiceProduct): PgaAgency[] => {
  const docs = Array.isArray(row.all_results) ? row.all_results : [];
  const agencies = new Set<PgaAgency>(
    Array.isArray(row.pga_agencies)
      ? row.pga_agencies.filter((agency): agency is PgaAgency =>
          PRODUCT_PGA_AGENCIES.includes(agency as PgaAgency),
        )
      : [],
  );
  docs.forEach((doc) => {
    const rawLens = doc['BITE Lens'] ?? doc.bite_lens ?? doc.lens ?? '';
    const lenses = Array.isArray(rawLens) ? rawLens : [rawLens];
    if (
      !lenses.some((lens) => String(lens).trim().toLowerCase() === 'pga codes')
    )
      return;
    const agency = [
      doc.Agency ?? doc.agency ?? '',
      doc['List Name'] ?? doc.list_name ?? '',
      doc.Program ?? doc.program ?? '',
    ]
      .map(String)
      .join(' ')
      .toUpperCase();
    PRODUCT_PGA_AGENCIES.forEach((candidate) => {
      if (
        PRODUCT_PGA_AGENCY_ALIASES[candidate].some((alias) =>
          agency.includes(alias),
        )
      ) {
        agencies.add(candidate);
      }
    });
    const flagCode = String(
      doc['PGA Flag Code'] ??
        doc['PGA Code'] ??
        doc.pga_flag_code ??
        doc.pga_code ??
        '',
    )
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
    PRODUCT_PGA_CODE_AGENCIES.forEach(({ prefix, agencies: mapped }) => {
      if (flagCode.startsWith(prefix)) {
        mapped.forEach((candidate) => agencies.add(candidate));
      }
    });
  });
  return [...agencies];
};

export const toImportProduct = (
  row: ImportServiceProduct,
  importDatabaseId: string,
): ImportProduct => {
  const systemIssues = Array.isArray(row.issues)
    ? row.issues.filter(
        (issue): issue is string => typeof issue === 'string' && !!issue.trim(),
      )
    : [];
  const clearedIssues = Array.isArray(row.clear_flags)
    ? row.clear_flags.filter(
        (issue): issue is string => typeof issue === 'string' && !!issue.trim(),
      )
    : [];
  const cleared = new Set(clearedIssues);
  const activeIssues = systemIssues.filter((issue) => !cleared.has(issue));
  const issueTypes = new Set<ImportIssueType>();
  activeIssues.forEach((issue) => {
    issueTypes.add(
      /(?:missing|invalid)\s+hts/i.test(issue)
        ? 'hts_review_required'
        : 'compliance_flag',
    );
  });

  const classification = row.tariff_result?.insights?.htsClassification;
  if (
    classification?.requiresAttention === true ||
    row.hts_requires_attention === true
  ) {
    issueTypes.add('hts_review_required');
  }

  const updatedAt = row.updated_at ? new Date(row.updated_at).getTime() : null;
  // A screening run only inserts a new ProductScreeningResult when the
  // product's matches actually changed; unchanged products just get
  // last_screened_at bumped. Take whichever timestamp is newer, so an edit that
  // doesn't alter the match set still clears once the product is re-screened.
  // Mirrors the analysis_required filter SQL in the product service.
  const screeningTimes = [
    row.last_screened_at,
    row.product_screening_created_at,
  ]
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((value) => Number.isFinite(value));
  const screenedAt = screeningTimes.length ? Math.max(...screeningTimes) : null;
  if (
    screenedAt === null ||
    (updatedAt !== null && Number.isFinite(updatedAt) && updatedAt > screenedAt)
  ) {
    issueTypes.add('analysis_required');
  }

  const tariffChanged =
    row.tariff_diff?.changed === true || row.tariff_diff?.changed === 'true';
  const tariffScreenedAt = row.tariff_screen_date
    ? new Date(row.tariff_screen_date).getTime()
    : null;
  const tariffSavedAt = row.tariff_saved_at
    ? new Date(row.tariff_saved_at).getTime()
    : null;
  if (
    tariffChanged &&
    (tariffScreenedAt === null ||
      tariffSavedAt === null ||
      tariffSavedAt <= tariffScreenedAt + 1000)
  ) {
    issueTypes.add('tariff_change');
  }

  return {
    id: row.id,
    sku: row.model_number || '',
    name: row.name,
    description: row.description ?? '',
    htsCode: normalizeHtsForSearch(row.hts_code) ?? row.hts_code ?? '',
    countryOfOrigin: row.country_of_origin ?? '',
    supplierId: '',
    manufacturerId: '',
    declaredValue: row.product_value ?? 0,
    totalCost: feeFreeTotalCost(row.tariff_result?.math),
    unitOfMeasure:
      row.unit_of_measure ?? row.additional_columns?.unit_of_measure ?? '',
    importDatabaseId: row.database_id ?? importDatabaseId,
    databaseProductId: row.database_product_id ?? undefined,
    databaseName: row.database_name ?? undefined,
    complianceScore: row.compliance_score ?? 0,
    complianceAssessed:
      Boolean(row.product_screening_created_at) ||
      (row.compliance_score ?? 0) > 0,
    riskLevel: deriveRiskLevel(row),
    pgaFlags: derivePgaFlags(row),
    pgaPrograms: Array.isArray(row.pga_programs) ? row.pga_programs : null,
    pgaFlagCodes: Array.isArray(row.pga_flag_codes) ? row.pga_flag_codes : null,
    adcvdCases: Array.isArray(row.adcvd_cases) ? row.adcvd_cases : null,
    section301: false,
    section301Rate: 0,
    section232: false,
    section232Rate: 0,
    additionalDutyPct: 0,
    fta: 'none',
    ftaEligible: false,
    ftaEvaluated: false,
    uflpaRisk: 'none',
    category: row.category ?? undefined,
    hierarchyType: row.hierarchy_type ?? undefined,
    childProductCount: Number(row.child_products ?? 0),
    databaseStructure: row.database_structure ?? undefined,
    relationshipId: row.relationship_id ?? undefined,
    relationshipQuantity:
      row.relationship_quantity == null
        ? undefined
        : Number(row.relationship_quantity),
    relationshipUnitOfMeasure: row.relationship_unit_of_measure ?? undefined,
    relationshipUnitValue:
      row.relationship_unit_value == null
        ? undefined
        : Number(row.relationship_unit_value),
    manufacturer: row.manufacturer ?? undefined,
    vendor: row.vendor ?? undefined,
    systemIssues: activeIssues,
    clearedIssues,
    issueTypes: [...issueTypes],
    ...deriveTariffDisplayFields(row.tariff_result, row.product_value),
    ...(row.tariff_result == null ? deriveCompactTariffDisplayFields(row) : {}),
  };
};

/**
 * Builds the complete detail model for a real product. The first argument is
 * the currently displayed classification code so an in-session HTS selection
 * can be previewed without reintroducing the demo-only buildProductDetail
 * object as a base.
 */
export interface ImportServiceProductChildrenResult {
  success: boolean;
  children: ImportServiceProduct[];
  error?: string;
}

export const fetchImportProductChildren = async (
  productId: string,
  databaseId: string,
): Promise<ImportServiceProductChildrenResult> => {
  const { success, result } = await ProductServiceAPI.getProductsByParentId(
    productId,
    databaseId,
  );

  if (!success) {
    return {
      success: false,
      children: [],
      error: typeof result === 'string' ? result : 'Failed to load components',
    };
  }

  return { success: true, children: Array.isArray(result) ? result : [] };
};

/**
 * Shape returned by GET /product/:databaseProductId/screening (biteservices
 * product service — see product.service.ts#getLatestScreeningResultByDatabaseProductId).
 * `ai_summary` is a single object (not an array — that pattern belongs to the
 * separate entity-screening path) from an external AI service, so every
 * field is optional/defensive. `denied_party_results` carries raw OpenSearch
 * docs with Title-Case field names (plus snake_case fallbacks).
 */
export interface ImportServiceScreeningResult {
  id?: string | null;
  screening_result_id?: string | null;
  database_product_id?: string | null;
  created_at?: string | null;
  last_screening_date?: string | null;
  hit_count?: number | null;
  new_total?: number | null;
  is_base_snapshot?: boolean | null;
  diff_added_count?: number | null;
  diff_removed_count?: number | null;
  diff_modified_count?: number | null;
  ai_summary?: {
    product_name?: string | null;
    risk?: string | null;
    risk_level?: string | null;
    match_probability?: number | null;
    explanation?: string | null;
    best_match?: string | null;
    trade_category?: string | null;
    screenDate?: string | null;
    top_matches?: {
      id?: string | null;
      name: string;
      confidence: number;
    }[];
    risk_assessment?: {
      overall_risk?: string | null;
      rationale?: string | null;
      high_lists?: string[];
      per_match?: Array<{
        doc_id?: string;
        risk?: string;
        rules_triggered?: string[];
      }>;
    } | null;
    error?: string | null;
  } | null;
  denied_party_results?: {
    docs?: Record<string, unknown>[];
  } | null;
  // Decompressed server-side (product.service.ts#getLatestScreeningResultByDatabaseProductId).
  // Each doc is a raw OpenSearch commodity hit with Title-Case keys ('BITE Lens',
  // 'Agency', 'List Name', 'HS Code', 'Name of Entity', 'Source Country', ...).
  all_results?: Record<string, unknown>[];
  pga_flag_codes?: string[] | null;
  // Active AD/CVD order numbers for the product's origin.
  adcvd_cases?: string[] | null;
}

export interface ImportServiceScreeningHistoryEntry {
  id: string;
  screening_result_id?: string | null;
  status: string;
  screened_by?: string | null;
  screening_reason?: string | null;
  started_at: string;
  completed_at?: string | null;
  hit_count?: number | null;
  new_total?: number | null;
  is_base_snapshot?: boolean | null;
  diff_added_count?: number | null;
  diff_removed_count?: number | null;
  diff_modified_count?: number | null;
  ai_status?: string | null;
  verification_status?: string | null;
  verification_phase?: string | null;
  ai_summary?: ImportServiceScreeningResult['ai_summary'];
  denied_party_match_count: number;
  error?: string | null;
  chunk_errors?: unknown[];
}

export interface ImportServiceAuditEvent {
  id: string;
  action: string;
  category: string;
  level: number;
  correlation_id?: string | null;
  message: string;
  user_id?: string | null;
  user_name?: string | null;
  is_system: boolean;
  occurred_at: string;
  metadata?: Record<string, unknown> | null;
}

export interface ImportServiceScreeningHistory {
  history: ImportServiceScreeningHistoryEntry[];
  auditEvents: ImportServiceAuditEvent[];
  total: number;
  loaded: number;
}

export interface ImportServiceScreeningHistoryResult {
  success: boolean;
  result: ImportServiceScreeningHistory;
  error?: string;
}

export interface ImportServiceScreeningResultResult {
  success: boolean;
  screening: ImportServiceScreeningResult | null;
  error?: string;
}

export interface ImportTariffExplanationContext {
  hsCode: string;
  generalRate: string;
  column2Rate: string;
  specialDutyRate: string;
}

export interface ImportTariffPreview {
  tariffResult: any | null;
  explanationContext: ImportTariffExplanationContext | null;
  unavailableReason?: string | null;
}

export interface ImportTariffPreviewResult {
  success: boolean;
  preview: ImportTariffPreview | null;
  error?: string;
}

export interface ImportTariffHistorySnapshot {
  baseDutyCost: number | null;
  totalDutyCost: number | null;
  totalCost: number | null;
  baseDutyRate: string | null;
  additionalDutyRate: number | null;
  additionalDutyCost: number | null;
  mpf: number | null;
  hmf: number | null;
  dutiesAndFees: number | null;
}

export interface ImportTariffStackSnapshot {
  id: string | null;
  label: string;
  value: number | null;
  duty: number | null;
  order: number | null;
  rows: Array<Record<string, unknown>>;
}

export interface ImportTariffHistoryEntry {
  id: string;
  screeningResultId: string | null;
  changedAt: string;
  savedAt: string;
  /**
   * When this change took (or, for a lapsed program, stopped taking) effect
   * per the tariff schedule -- distinct from `changedAt`, which is only when
   * a screening happened to observe it. Null when the change isn't tied to a
   * single dated rule (e.g. a base duty rate or fee change).
   */
  effectiveAt: string | null;
  heading: string;
  previousValue: string;
  currentValue: string;
  snapshot: ImportTariffHistorySnapshot;
  previousTariffStack: ImportTariffStackSnapshot | null;
  currentTariffStack: ImportTariffStackSnapshot | null;
  /** Alias for the prior stack represented by the changelog snapshot. */
  tariffStack: ImportTariffStackSnapshot | null;
}

export interface ImportTariffHistoryResult {
  success: boolean;
  history: ImportTariffHistoryEntry[];
  lastCheckedAt: string | null;
  error?: string;
}

export const fetchImportProductTariffHistory = async (
  databaseProductId: string,
  limit = 20,
): Promise<ImportTariffHistoryResult> => {
  const { success, result } = await ProductServiceAPI.getProductTariffHistory(
    databaseProductId,
    {
      limit,
    },
  );

  if (!success) {
    return {
      success: false,
      history: [],
      lastCheckedAt: null,
      error:
        typeof result === 'string' ? result : 'Failed to load tariff history',
    };
  }

  return {
    success: true,
    history: Array.isArray(result?.history) ? result.history : [],
    lastCheckedAt:
      typeof result?.lastCheckedAt === 'string' ? result.lastCheckedAt : null,
  };
};

export type ImportHtsClassificationStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'not_yet_effective'
  | 'not_found'
  | 'ambiguous';

export interface ImportHtsHistoryEvent {
  id: string;
  kind: 'schedule_update' | 'scheduled_expiration';
  date: string;
  effectiveDate: string;
  summary: string;
  previousRate: string | null;
  currentRate: string | null;
}

export interface ImportHtsClassification {
  code: string;
  formattedCode: string;
  asOf: string;
  status: ImportHtsClassificationStatus;
  valid: boolean;
  requiresAttention: boolean;
  reason: string;
  changedDate: string | null;
  checkedAt: string;
  currentRecord: {
    description: string;
    generalDutyRate: string;
    effectiveFrom: string | null;
    effectiveTo: string | null;
  } | null;
  history: ImportHtsHistoryEvent[];
}

export interface ImportHtsClassificationResult {
  success: boolean;
  classification: ImportHtsClassification | null;
  error?: string;
}

export interface ImportHtsRecommendation {
  code: string;
  formattedCode: string;
  description: string;
  confidence: number;
  rationale: string;
  recommended: boolean;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

export interface ImportHtsRecommendationsResult {
  success: boolean;
  classification: ImportHtsClassification | null;
  recommendations: ImportHtsRecommendation[];
  rankingMethod: 'semantic_and_rules' | 'rules' | null;
  error?: string;
}

export const fetchImportProductHtsClassification = async (
  databaseProductId: string,
): Promise<ImportHtsClassificationResult> => {
  const { success, result } =
    await ProductServiceAPI.getProductHtsClassification(databaseProductId);
  if (!success) {
    return {
      success: false,
      classification: null,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load HTS classification',
    };
  }
  return {
    success: true,
    classification: result as ImportHtsClassification,
  };
};

export const fetchImportProductHtsRecommendations = async (
  databaseProductId: string,
): Promise<ImportHtsRecommendationsResult> => {
  const { success, result } =
    await ProductServiceAPI.getProductHtsRecommendations(databaseProductId);
  if (!success) {
    return {
      success: false,
      classification: null,
      recommendations: [],
      rankingMethod: null,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to generate HTS recommendations',
    };
  }
  return {
    success: true,
    classification:
      result?.classification && typeof result.classification === 'object'
        ? (result.classification as ImportHtsClassification)
        : null,
    recommendations: Array.isArray(result?.recommendations)
      ? result.recommendations
      : [],
    rankingMethod:
      result?.rankingMethod === 'semantic_and_rules'
        ? 'semantic_and_rules'
        : 'rules',
  };
};

export interface ImportCrossRulingsResult {
  success: boolean;
  crossRulings: string[];
  error?: string;
}

export const fetchImportProductCrossRulings = async (
  databaseProductId: string,
): Promise<ImportCrossRulingsResult> => {
  const { success, result } = await ProductServiceAPI.getProductCrossRulings(
    databaseProductId,
  );

  if (!success) {
    return {
      success: false,
      crossRulings: [],
      error:
        typeof result === 'string' ? result : 'Failed to load CROSS rulings',
    };
  }

  return {
    success: true,
    crossRulings: Array.isArray(result?.cross_data_links)
      ? result.cross_data_links.filter(
          (link: unknown): link is string =>
            typeof link === 'string' && Boolean(link.trim()),
        )
      : [],
  };
};

export const fetchImportProductTariffPreview = async (
  databaseProductId: string,
  options: {
    enabledExclusions?: Record<string, boolean>;
    enabledFtas?: Record<string, boolean>;
  } = {},
): Promise<ImportTariffPreviewResult> => {
  const { success, result } = await ProductServiceAPI.previewProductTariff(
    databaseProductId,
    options,
  );

  if (!success) {
    return {
      success: false,
      preview: null,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to recalculate tariff results',
    };
  }

  return { success: true, preview: result ?? null };
};

export const fetchImportProductScreening = async (
  databaseProductId: string,
): Promise<ImportServiceScreeningResultResult> => {
  const { success, result } = await ProductServiceAPI.getProductScreening(
    databaseProductId,
  );

  if (!success) {
    return {
      success: false,
      screening: null,
      error:
        typeof result === 'string' ? result : 'Failed to load screening result',
    };
  }

  return { success: true, screening: result ?? null };
};

export const fetchImportProductScreeningHistory = async (
  databaseProductId: string,
  limit = 50,
  offset = 0,
): Promise<ImportServiceScreeningHistoryResult> => {
  const { success, result } =
    await ProductServiceAPI.getProductScreeningHistory(databaseProductId, {
      limit,
      offset,
    });

  if (!success) {
    return {
      success: false,
      result: { history: [], auditEvents: [], total: 0, loaded: 0 },
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load screening history',
    };
  }

  const history = Array.isArray(result?.history) ? result.history : [];
  const auditEvents = Array.isArray(result?.audit_events)
    ? result.audit_events
    : [];
  return {
    success: true,
    result: {
      history,
      auditEvents,
      total: Number(
        result?.pagination?.activity_total ?? result?.pagination?.total ?? 0,
      ),
      loaded: history.length + auditEvents.length,
    },
  };
};

export interface ImportComplianceFlagUpdateResult {
  success: boolean;
  update?: {
    compliance_score: number;
    issues: string[];
    clear_flags: string[];
  };
  error?: string;
}

export const updateImportProductComplianceFlag = async (
  databaseProductId: string,
  flag: string,
  cleared: boolean,
): Promise<ImportComplianceFlagUpdateResult> => {
  const { success, result } =
    await ProductServiceAPI.updateProductComplianceFlag(
      databaseProductId,
      flag,
      cleared,
    );

  if (!success) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to update compliance flag',
    };
  }

  return {
    success: true,
    update: {
      compliance_score: Number(result?.compliance_score ?? 0),
      issues: Array.isArray(result?.issues) ? result.issues : [],
      clear_flags: Array.isArray(result?.clear_flags) ? result.clear_flags : [],
    },
  };
};

const pickFirst = (
  doc: Record<string, unknown>,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = doc[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
};

const pickFirstUrl = (
  doc: Record<string, unknown>,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = doc[key];
    const candidates = Array.isArray(value) ? value : [value];
    for (const candidate of candidates) {
      if (typeof candidate !== 'string' || !candidate.trim()) continue;
      const trimmed = candidate.trim();
      try {
        const url = new URL(trimmed);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          return trimmed;
        }
      } catch {
        // Screening documents are external input. Ignore malformed and
        // relative values instead of exposing them as clickable links.
      }
    }
  }
  return undefined;
};

/**
 * Lenses the Program Codes box's tabs read directly (PGA / Forced Labor /
 * AD-CVD) — everything else buckets into `other`, one group per lens name.
 * Kept as a Set (not a static enum) because the live commodity index carries
 * more lens names than any static list enumerates.
 */
const NAMED_COMMODITY_LENSES = new Set([
  'PGA Codes',
  'Forced Labor Commodities',
  'AD CVD Commodities',
  'AD CVD Entities',
]);

const EU_IMPORT_LIST_NAMES = [
  'Regulation on Deforestation Free Products',
  'Carbon Border Adjustment Mechanism',
  'Trade Defence Investigations',
  'EU Product Safety List',
] as const;

type EuImportListName = (typeof EU_IMPORT_LIST_NAMES)[number];

const EU_IMPORT_LIST_NAME_SET = new Set<string>(EU_IMPORT_LIST_NAMES);

export interface CommodityLensGroups {
  pga: BiteLensGroup[];
  forcedLabor: ProductDetail['forcedLabor'];
  adcvd: ProductDetail['adcvd'];
  euImports: BiteLensGroup[];
  other: BiteLensGroup[];
  // Raw AD/CVD hits, so a caller can rebuild `adcvd` once it knows the
  // product's applicable orders.
  adcvdDocs: Record<string, unknown>[];
}

const htsCandidates = (value: unknown): string[] => {
  if (typeof value !== 'string' && typeof value !== 'number') return [];
  const raw = String(value).trim();
  if (!raw) return [];

  // Most index values contain one dotted or digit-only code. Only treat the
  // whole value as one code when it has no descriptive text, so separate
  // codes are never accidentally concatenated into a false match.
  // UPDATE: Changed {1,3} to {0,3} to allow exactly 4 digits.
  if (/^\d{4}(?:[.\s-]?\d{2}){0,3}$/.test(raw)) {
    return [raw.replace(/\D/g, '')];
  }

  // UPDATE: Added |\b\d{4}\b at the end of the regex to capture standalone 4-digit headings.
  const matches = raw.match(
    /\b\d{4}(?:\.\d{2}){1,3}\b|\b\d{10}\b|\b\d{8}\b|\b\d{6}\b|\b\d{4}\b/g,
  );
  return [...new Set((matches ?? []).map((code) => code.replace(/\D/g, '')))];
};

const matchesEuImportHts = (
  listName: EuImportListName,
  sourceHts: unknown,
  productHtsCode: string,
): boolean => {
  const productDigits = productHtsCode.replace(/\D/g, '');
  if (![6, 8, 10].includes(productDigits.length)) return false;

  return htsCandidates(sourceHts).some((sourceDigits) => {
    if (
      listName === 'Regulation on Deforestation Free Products' ||
      listName === 'Carbon Border Adjustment Mechanism'
    ) {
      // UPDATE: Support 4-digit heading prefix matches alongside the existing 6-digit matches
      if (sourceDigits.length === 4) {
        return productDigits.startsWith(sourceDigits);
      }
      return (
        sourceDigits.length >= 6 &&
        productDigits.slice(0, 6) === sourceDigits.slice(0, 6)
      );
    }

    if (listName === 'Trade Defence Investigations') {
      if (sourceDigits.length === 10 && productDigits.length === 10) {
        return sourceDigits === productDigits;
      }
      return (
        sourceDigits.length === 8 && productDigits.startsWith(sourceDigits)
      );
    }

    // EU Product Safety follows the existing Import Screening behavior: a
    // four-, six- or eight-digit list code covers its children, while ten digits
    // require an exact match.
    // UPDATE: Include 4-digit length in the array of valid source lengths.
    if (![4, 6, 8, 10].includes(sourceDigits.length)) return false;
    return productDigits.startsWith(sourceDigits);
  });
};

const commodityDocKey = (doc: Record<string, unknown>): string =>
  JSON.stringify(
    Object.entries(doc).sort(([left], [right]) => left.localeCompare(right)),
  );

const commodityTradeTypes = (doc: Record<string, unknown>): string[] => {
  const value = doc['Trade Type'] ?? doc.trade_type ?? doc.tradeType;
  const values = Array.isArray(value) ? value : [value];

  return values
    .flatMap((item) => String(item ?? '').split(/[;,|]/))
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
};

const isCanadaOnlyImportDoc = (doc: Record<string, unknown>): boolean => {
  const tradeTypes = commodityTradeTypes(doc);
  return tradeTypes.includes('ca import') && !tradeTypes.includes('us import');
};

const dedupeCommodityDocs = (
  docs: Record<string, unknown>[],
): Record<string, unknown>[] => {
  const seen = new Set<string>();
  return docs.filter((doc) => {
    const key = commodityDocKey(doc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Real docs carry 'List Name', not 'List' — alias it so the existing
// flattenMatches() list column and resolvePgaCode()'s Program/List fallback
// (used to disambiguate NMFS/APHIS/EPA sub-programs) both pick it up without
// changing either function. Row fields are otherwise passed through
// unchanged; column labels/fallbacks are a documented follow-up.
const toBiteLensGroup = (
  name: string,
  docs: Record<string, unknown>[],
): BiteLensGroup => ({
  name,
  total: docs.length,
  newCount: 0,
  columns: ['HS Code', 'List Name', 'Agency'],
  rows: docs.map((doc) => ({
    ...(doc as Record<string, string | number>),
    List:
      typeof doc['List Name'] === 'string' ? (doc['List Name'] as string) : '',
  })),
});

export const groupStoredScreeningDocsByLens = (
  docs: Record<string, unknown>[],
): BiteLensGroup[] => {
  const byLens = new Map<string, Record<string, unknown>[]>();

  for (const doc of docs) {
    const lens =
      pickFirst(doc, ['BITE Lens', 'bite_lens', 'lens']) ??
      'Other Screening Results';
    const bucket = byLens.get(lens) ?? [];
    bucket.push(doc);
    byLens.set(lens, bucket);
  }

  return [...byLens.entries()]
    .map(([lens, lensDocs]) => toBiteLensGroup(lens, lensDocs))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Groups raw commodity/screening docs (Title-Case OpenSearch fields) by their
 * 'BITE Lens' value into the shapes the Program Codes box's five tabs read.
 * Shared by the live commodity search (Slice 9) — the sole caller today,
 * kept as a standalone function so a future stored-screening caller (if any)
 * doesn't have to re-derive this grouping.
 */
const ADCVD_ORDERS_LIST =
  'U.S. Anti Dumping Countervailing Duty Orders (by Order)';

const adcvdOrderNumbers = (doc: Record<string, unknown>): string[] =>
  (pickFirst(doc, ['Order Number(s)', 'Case Number']) ?? '')
    .split(/[,;|\s]+/)
    .map((order) => order.trim().toUpperCase())
    .filter(Boolean);

/**
 * AD/CVD card for a product. `applicableOrders` are the active orders for the
 * product's country of origin that screening stored (adcvd_cases, see the
 * product service's deriveAdcvdCases); the same set drives the table chip and
 * exports. An HTS code usually matches orders for many countries, so the raw
 * hits stay in `groups` as evidence but only applicable orders count. Null
 * means the product hasn't been screened, so scope can't be decided yet.
 */
export const buildAdcvdDetail = (
  docs: Record<string, unknown>[],
  applicableOrders: string[] | null | undefined,
): ProductDetail['adcvd'] => {
  const commodityDocs = docs.filter(
    (doc) => doc['BITE Lens'] !== 'AD CVD Entities',
  );
  const entityDocs = docs.filter(
    (doc) => doc['BITE Lens'] === 'AD CVD Entities',
  );
  const groups: BiteLensGroup[] = [
    ...(commodityDocs.length > 0
      ? [toBiteLensGroup('AD CVD Commodities', commodityDocs)]
      : []),
    ...(entityDocs.length > 0
      ? [toBiteLensGroup('AD CVD Entities', entityDocs)]
      : []),
  ];
  const reasoning =
    "Active AD/CVD orders whose country matches the product's country of origin. Confirm the goods fall within each order's scope.";
  const otherRecords = `${docs.length} AD/CVD record${
    docs.length === 1 ? '' : 's'
  } on this HTS code`;

  if (!applicableOrders) {
    return {
      exposure: false,
      cases: [],
      scopeDetermination:
        docs.length > 0
          ? `${otherRecords} found. Screen the product to check them against its country of origin.`
          : 'No AD/CVD matches found in screening.',
      reasoning,
      severity: docs.length > 0 ? 'warning' : 'success',
      groups,
    };
  }

  const applicable = new Set(applicableOrders);
  const casesByOrder = new Map<
    string,
    ProductDetail['adcvd']['cases'][number]
  >();
  docs.forEach((doc) => {
    if (pickFirst(doc, ['List Name']) !== ADCVD_ORDERS_LIST) return;
    adcvdOrderNumbers(doc)
      .filter((order) => applicable.has(order) && !casesByOrder.has(order))
      .forEach((order) =>
        casesByOrder.set(order, {
          caseNumber: order,
          product:
            pickFirst(doc, ['Principal Official Case Name', 'Product Name']) ??
            '',
          // Hits carry no deposit rate.
          rate: null,
          producer: pickFirst(doc, ['Name of Entity']) ?? '—',
          country: pickFirst(doc, ['BITE-Identified Risk Country']) ?? '—',
        }),
      );
  });
  // Orders stored at screening but absent from these hits (e.g. a newer live
  // search) still apply.
  applicableOrders.forEach((order) => {
    if (!casesByOrder.has(order)) {
      casesByOrder.set(order, {
        caseNumber: order,
        product: '',
        rate: null,
        producer: '—',
        country: '—',
      });
    }
  });
  const cases = [...casesByOrder.values()];

  return {
    exposure: cases.length > 0,
    cases,
    scopeDetermination:
      cases.length > 0
        ? `${cases.length} active AD/CVD order${
            cases.length === 1 ? '' : 's'
          } for this product's country of origin.`
        : docs.length > 0
        ? `No active AD/CVD order for this product's country of origin. ${otherRecords} apply to other countries or are inactive.`
        : 'No AD/CVD matches found in screening.',
    reasoning,
    severity: cases.length > 0 ? 'danger' : 'success',
    groups,
  };
};

export const groupCommodityDocsByLens = (
  docs: Record<string, unknown>[],
  productHtsCode?: string,
  adcvdOrders?: string[] | null,
): CommodityLensGroups => {
  const euDocsByLens = new Map<string, Record<string, unknown>[]>();
  const byLens = new Map<string, Record<string, unknown>[]>();
  const canadaPgaDocs: Record<string, unknown>[] = [];
  for (const doc of docs) {
    const listName =
      typeof doc?.['List Name'] === 'string'
        ? (doc['List Name'] as string)
        : '';
    const lens =
      typeof doc?.['BITE Lens'] === 'string'
        ? (doc['BITE Lens'] as string)
        : null;
    if (!lens) continue;

    // The PGA tab represents U.S. import filing requirements. Canadian PGA
    // records use the same OpenSearch lens, so route Canada-only matches to
    // Other where they remain visible without being presented as U.S. flags.
    if (lens === 'PGA Codes' && isCanadaOnlyImportDoc(doc)) {
      canadaPgaDocs.push(doc);
      continue;
    }

    if (EU_IMPORT_LIST_NAME_SET.has(listName)) {
      if (
        productHtsCode &&
        !matchesEuImportHts(
          listName as EuImportListName,
          doc['HS Code'],
          productHtsCode,
        )
      ) {
        continue;
      }
      const euBucket = euDocsByLens.get(lens) ?? [];
      euBucket.push(doc);
      euDocsByLens.set(lens, euBucket);
      continue;
    }

    const bucket = byLens.get(lens) ?? [];
    bucket.push(doc);
    byLens.set(lens, bucket);
  }

  const pgaDocs = byLens.get('PGA Codes') ?? [];
  const pga: BiteLensGroup[] =
    pgaDocs.length > 0 ? [toBiteLensGroup('PGA Codes', pgaDocs)] : [];

  const forcedLaborDocs = byLens.get('Forced Labor Commodities') ?? [];
  const forcedLaborGroups =
    forcedLaborDocs.length > 0
      ? [toBiteLensGroup('Forced Labor Commodities', forcedLaborDocs)]
      : [];
  const forcedLabor: ProductDetail['forcedLabor'] = {
    result:
      forcedLaborDocs.length > 0
        ? `${forcedLaborDocs.length} match${
            forcedLaborDocs.length === 1 ? '' : 'es'
          } found in screening`
        : 'No matches found in screening',
    basis: 'Real-time match on the Forced Labor Commodities screening lens.',
    entities: forcedLaborDocs.map(
      (doc) => pickFirst(doc, ['Name of Entity']) ?? 'Unknown entity',
    ),
    severity: forcedLaborDocs.length > 0 ? 'danger' : 'success',
    groups: forcedLaborGroups,
  };

  const adcvdDocs = [
    ...(byLens.get('AD CVD Commodities') ?? []),
    ...(byLens.get('AD CVD Entities') ?? []),
  ];
  const adcvd = buildAdcvdDetail(adcvdDocs, adcvdOrders);

  const other: BiteLensGroup[] = [];
  if (canadaPgaDocs.length > 0) {
    other.push(toBiteLensGroup('Canada PGA Codes', canadaPgaDocs));
  }
  for (const [lensName, lensDocs] of byLens) {
    if (NAMED_COMMODITY_LENSES.has(lensName)) continue;
    other.push(toBiteLensGroup(lensName, lensDocs));
  }

  const euImports = [...euDocsByLens.entries()]
    .map(([lensName, lensDocs]) => toBiteLensGroup(lensName, lensDocs))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { pga, forcedLabor, adcvd, euImports, other, adcvdDocs };
};

export interface ImportCommodityScreeningResult {
  success: boolean;
  groups?: CommodityLensGroups;
  error?: string;
}

/**
 * Live commodity/regulatory-list search for the Program Codes box's
 * assistant table (Slice 9). The persisted screening pipeline now handles PGA
 * as an origin-independent HTS lookup, but this view remains live so users can
 * request the latest index contents between scheduled database screenings.
 * Jurisdictions are queried separately so U.S., Canadian, and EU results can
 * be routed to their intended tabs without one jurisdiction consuming another
 * jurisdiction's result limit.
 */
export const fetchImportCommodityScreening = async (
  htsCode: string,
): Promise<ImportCommodityScreeningResult> => {
  const primary = normalizeHtsForSearch(htsCode) ?? htsCode;
  const [usResponse, canadaResponse, euResponse] = await Promise.all([
    ProductServiceAPI.searchCommodity({
      indices: ['bite_app_commodities_combined'],
      primary,
      tradeType: 'US Import',
      qty: 250,
      agg: false,
    }),
    ProductServiceAPI.searchCommodity({
      indices: ['bite_app_commodities_combined'],
      primary,
      lens: ['PGA Codes'],
      tradeType: 'CA Import',
      qty: 250,
      agg: false,
    }),
    ProductServiceAPI.searchCommodity({
      indices: ['bite_app_commodities_combined'],
      primary,
      tradeType: 'EU Import',
      listName: [...EU_IMPORT_LIST_NAMES],
      qty: 250,
      agg: false,
    }),
  ]);

  if (!usResponse.success && !canadaResponse.success && !euResponse.success) {
    const result =
      usResponse.result ?? canadaResponse.result ?? euResponse.result;
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to load commodity screening results',
    };
  }

  const extractDocs = (result: any): Record<string, unknown>[] => {
    const hits = result?.result?.hits?.hits;
    return Array.isArray(hits)
      ? hits.map((hit: any) => {
          const source = hit?._source ?? {};
          return {
            ...source,
            // Keep the source record addressable when a user attaches it to a
            // PGA flag. The index id is copied into the existing regulatory
            // record shape only when the source does not already supply one.
            ...(!source['BITE Record ID'] && hit?._id
              ? { 'BITE Record ID': String(hit._id) }
              : {}),
          };
        })
      : [];
  };
  const docs = dedupeCommodityDocs([
    ...extractDocs(usResponse.result),
    ...extractDocs(canadaResponse.result),
    ...extractDocs(euResponse.result),
  ]);

  return {
    success: true,
    groups: groupCommodityDocsByLens(docs, htsCode),
  };
};

export const applyRealProductDetail = (
  displayedHtsCode: string,
  row: ImportServiceProduct,
  children?: ImportServiceProduct[],
  screening?: ImportServiceScreeningResult | null,
): ProductDetail => {
  const tariffInputs = {
    smeltCountry: row.country_of_smelt_pour ?? '',
    copperPct: row.copper_percent ?? 0,
    copperValue: row.copper_value ?? 0,
    aluminumPct: row.aluminum_percent ?? 0,
    aluminumValue: row.aluminum_value ?? 0,
    steelPct: row.steel_percent ?? 0,
    steelValue: row.steel_value ?? 0,
    hierarchyType: row.hierarchy_type ?? '',
    category: row.category ?? '',
    manufacturer: row.manufacturer ?? '',
    vendor: row.vendor ?? '',
  };

  const productDesc = row.tariff_result?.base?.productDesc;
  const classification = {
    code: displayedHtsCode || 'Unclassified',
    confidence: 0,
    description:
      typeof productDesc === 'string' && productDesc
        ? productDesc
        : row.description ?? '',
    reasoning:
      typeof row.tariff_result?.status?.message === 'string'
        ? row.tariff_result.status.message
        : '',
    alternates: [],
  };

  // totalCost (declared value + duty, fees excluded) and totalDutyCost (duty
  // only) are distinct metrics.
  const math = row.tariff_result?.math;
  const baseDutyRateStr = row.tariff_result?.base?.baseDutyRate;
  const declaredValue = row.product_value ?? 0;
  const tariffLines = row.tariff_result?.results?.lines;
  const totalCost = feeFreeTotalCost(math);
  const totalDutyCost = toFiniteNumber(math?.dutyCost);
  const additionalDutyDollars =
    toFiniteNumber(math?.additionalDutyDollars) ?? 0;
  const cost = {
    baseRatePct:
      typeof baseDutyRateStr === 'string' ? parseRatePct(baseDutyRateStr) : 0,
    // Backed out of the engine's own total, not re-parsed from the rate.
    baseCost: math ? engineFigures(math).baseDuty : 0,
    // Derived from dollars, so round off float noise (14.21 / 49 * 100 is
    // 29.000000000000004) before it reaches the page or the PDF.
    additionalRatePct:
      math && declaredValue > 0
        ? Math.round((additionalDutyDollars / declaredValue) * 10000) / 100
        : 0,
    additionalCost: math ? additionalDutyDollars : 0,
    totalDutyCost: totalDutyCost ?? 0,
    totalCost: totalCost ?? declaredValue,
    unitValue: declaredValue,
  };

  // Duty line items come from results.lines (each paired with its own dollar
  // base), NOT results.summaryRows — summaryRows is a code-deduped flat view
  // derived from lines.flatMap(rows) that loses the per-line value pairing a
  // split product (e.g. a material-percentage split) needs for correct costs.
  const dutyLines: DutyLine[] =
    Array.isArray(tariffLines) && tariffLines.length > 0
      ? tariffLines.flatMap((line: any) =>
          Array.isArray(line.rows)
            ? line.rows.map((r: any) => ({
                subCode: r.code ?? '',
                label: r.type ?? '',
                ratePct: parseRatePct(r.rate),
                cost: (parseRatePct(r.rate) / 100) * (line.value ?? 0),
                ...(isPureAdValoremRate(r.rate)
                  ? {}
                  : { note: `Stored rate: ${r.rate}` }),
              }))
            : [],
        )
      : [];

  const statusMessage = row.tariff_result?.status?.message;
  const availableFtaPrograms = deriveAvailableFtaPrograms(row.tariff_result);
  const ftas: FtaItem[] =
    availableFtaPrograms.length > 0
      ? availableFtaPrograms.map((program) => ({
          program,
          status: 'Free' as const,
          ...(typeof statusMessage === 'string' ? { note: statusMessage } : {}),
        }))
      : [];

  const optionalExclusions = row.tariff_result?.results?.optionalExclusions;
  const enabledExclusionsMap = row.tariff_result?.results?.enabledExclusions;
  const exclusions: ExclusionItem[] =
    Array.isArray(optionalExclusions) && optionalExclusions.length > 0
      ? optionalExclusions.map((c: any) => ({
          subCode: c.codePretty ?? c.codeDigits ?? '',
          label: c.label ?? '',
          ratePct: typeof c.ratePct === 'number' ? c.ratePct : 0,
          applicability: (enabledExclusionsMap?.[c.codeDigits]
            ? 'applicable'
            : 'candidate') as ExclusionItem['applicability'],
        }))
      : [];

  const crossRulings: CrossRuling[] =
    Array.isArray(row.cross_rulings) && row.cross_rulings.length > 0
      ? row.cross_rulings.map((url, index) => ({
          id: `cross-${index}`,
          title: url,
          summary: '',
          date: '',
          applicability: 0,
          url,
        }))
      : [];

  // percent defaults to 0: the parent-id query doesn't carry a BOM
  // quantity/share (that lives on ProductStructure, not selected here) — a
  // documented gap, same treatment as other backend-absent fields above.
  const childComponents: ChildComponent[] =
    children && children.length > 0
      ? children.map((child) => ({
          name: child.name,
          hts: child.hts_code ?? '',
          percent: 0,
          origin: child.country_of_origin ?? '',
        }))
      : [];

  // ai_summary is the response from an external AI service — every field is
  // optional. A neutral summary is used when no real AI output exists; demo
  // content must never leak into a real product.
  const aiSummary = screening?.ai_summary;
  const rawRisk = aiSummary?.risk_level ?? aiSummary?.risk;
  const normalizedRisk = String(rawRisk ?? '')
    .trim()
    .toLowerCase();
  const risk: ProductDetail['ai']['risk'] =
    normalizedRisk === 'critical'
      ? 'Critical'
      : normalizedRisk === 'high'
      ? 'High'
      : normalizedRisk === 'medium'
      ? 'Medium'
      : 'Low';
  const keyFactors: string[] = [];
  if (aiSummary?.best_match) {
    keyFactors.push(`Best match: ${aiSummary.best_match}`);
  }
  if (aiSummary?.trade_category) {
    keyFactors.push(`Trade category: ${aiSummary.trade_category}`);
  }
  if (Array.isArray(aiSummary?.top_matches)) {
    for (const match of aiSummary.top_matches) {
      if (match?.name) {
        keyFactors.push(
          `${match.name} (${Math.round((match.confidence ?? 0) * 100)}%)`,
        );
      }
    }
  }
  const neutralAi: ProductDetail['ai'] = {
    risk: 'Low',
    burden: null,
    keyFactors: [],
    narrative: 'No AI screening summary is available for this product.',
    status: aiSummary?.error ? 'error' : 'unavailable',
    ...(aiSummary?.error ? { error: aiSummary.error } : {}),
  };
  const hasAiSummary = Boolean(
    aiSummary &&
      !aiSummary.error &&
      (aiSummary.risk_level ||
        aiSummary.risk ||
        aiSummary.best_match ||
        aiSummary.explanation ||
        typeof aiSummary.match_probability === 'number'),
  );
  const ai: ProductDetail['ai'] =
    aiSummary && hasAiSummary
      ? {
          ...neutralAi,
          status: 'available' as const,
          risk,
          ...(typeof aiSummary.explanation === 'string' && aiSummary.explanation
            ? { narrative: aiSummary.explanation }
            : {}),
          ...(keyFactors.length > 0 ? { keyFactors } : {}),
          ...(aiSummary.product_name
            ? { productName: aiSummary.product_name }
            : {}),
          ...(aiSummary.best_match ? { bestMatch: aiSummary.best_match } : {}),
          matchProbability:
            typeof aiSummary.match_probability === 'number'
              ? aiSummary.match_probability
              : null,
          ...(aiSummary.screenDate
            ? { screenedAt: aiSummary.screenDate }
            : screening?.created_at
            ? { screenedAt: screening.created_at }
            : {}),
          topMatches: Array.isArray(aiSummary.top_matches)
            ? aiSummary.top_matches
                .filter(
                  (match) =>
                    match &&
                    typeof match.name === 'string' &&
                    typeof match.confidence === 'number',
                )
                .map((match) => ({
                  ...(match.id ? { id: match.id } : {}),
                  name: match.name,
                  confidence: match.confidence,
                }))
            : [],
          ...(aiSummary.risk_assessment
            ? {
                riskAssessment: {
                  ...(aiSummary.risk_assessment.overall_risk
                    ? {
                        overallRisk: aiSummary.risk_assessment.overall_risk,
                      }
                    : {}),
                  ...(aiSummary.risk_assessment.rationale
                    ? { rationale: aiSummary.risk_assessment.rationale }
                    : {}),
                  highLists: Array.isArray(aiSummary.risk_assessment.high_lists)
                    ? aiSummary.risk_assessment.high_lists
                    : [],
                  perMatch: Array.isArray(aiSummary.risk_assessment.per_match)
                    ? aiSummary.risk_assessment.per_match.map((match) => ({
                        ...(match.doc_id ? { docId: match.doc_id } : {}),
                        ...(match.risk ? { risk: match.risk } : {}),
                        rulesTriggered: Array.isArray(match.rules_triggered)
                          ? match.rules_triggered
                          : [],
                      }))
                    : [],
                },
              }
            : {}),
        }
      : neutralAi;

  const deniedPartyDocs = screening?.denied_party_results?.docs;
  const deniedPartyMatches =
    Array.isArray(deniedPartyDocs) && deniedPartyDocs.length > 0
      ? deniedPartyDocs.map((doc) => ({
          entity:
            pickFirst(doc, ['Name of Entity', 'entity_name', 'name']) ?? '',
          list: pickFirst(doc, ['List Name', 'list_name', 'list']) ?? '',
          agency: pickFirst(doc, ['Agency', 'agency']) ?? '',
          country:
            pickFirst(doc, [
              'Source Country',
              'Country',
              'country',
              'source_country',
            ]) ?? '',
          date:
            pickFirst(doc, [
              'Date Updated on BITE Platform',
              'Date Added',
              'date_updated',
              'updated_at',
            ]) ?? '',
          source: pickFirst(doc, ['Source', 'source', 'Source Name']) ?? '',
          sourceUrl:
            pickFirstUrl(doc, [
              'Source URL',
              'Source URLs',
              'source_url',
              'source_urls',
              'URL',
              'url',
            ]) ?? '',
          score:
            pickFirst(doc, ['Match Score', 'Score', 'match_score', '_score']) ??
            '',
        }))
      : [];
  const storedScreeningGroups = groupStoredScreeningDocsByLens(
    Array.isArray(screening?.all_results) ? screening.all_results : [],
  );
  const storedCommodity = groupCommodityDocsByLens(
    Array.isArray(screening?.all_results) ? screening.all_results : [],
    undefined,
    screening?.adcvd_cases,
  );

  return {
    pga: [],
    forcedLabor: storedCommodity.forcedLabor,
    ftaDetail: {
      program: '',
      eligible: false,
      reasoning: 'No free trade agreement has been selected.',
      criteria: [],
    },
    adcvd: storedCommodity.adcvd,
    tariffReasoning:
      typeof row.tariff_result?.status?.message === 'string'
        ? row.tariff_result.status.message
        : '',
    tariffInputs,
    classification,
    cost,
    dutyLines,
    ftas,
    exclusions,
    crossRulings,
    childComponents,
    ai,
    deniedPartyMatches,
    riskFlags: [],
    htsHistory: [],
    biteLensGroups: storedCommodity.pga,
    screeningLensGroups: storedScreeningGroups,
    euImports: storedCommodity.euImports,
    other: storedCommodity.other,
    coBreakdown: row.country_of_origin
      ? [{ country: row.country_of_origin, percent: 100 }]
      : [],
    documents: [],
    comments: [],
  };
};

/**
 * Shape returned by GET /product/:databaseProductId/pga-flags (biteservices
 * product service). `created_at` is guaranteed (NOT NULL DEFAULT now() on
 * the column) — no fallback needed when mapping to AppliedPgaFlag.addedAt.
 */
export interface ImportServicePgaFlagRow {
  id: string;
  database_product_id: string;
  code: string;
  values: Record<string, unknown>;
  repeaters: Record<string, Record<string, unknown>[]>;
  documents: Array<{
    id: string;
    name: string;
    kind: string;
    size: number;
    uploadedAt: string;
  }>;
  created_at: string;
}

export interface ImportPgaDefinitionField {
  header: string;
  section: string;
  storageKey: string;
  inputType: 'text' | 'date' | 'yesno';
  uiTarget?:
    | { kind: 'value'; key: string }
    | {
        kind: 'repeater';
        group: string;
        index: number;
        key: string;
        path?: string[];
      };
}

export interface ImportPgaFlagDefinition {
  code: PgaCode;
  sourceCode: string;
  agency: string;
  displayName: string;
  fieldCount: number;
  sections: string[];
  fields: ImportPgaDefinitionField[];
}

export type ImportPgaFlagDefinitionSummary = Omit<
  ImportPgaFlagDefinition,
  'fields'
>;

export interface ImportPgaTemplatePreview {
  values: Record<string, unknown>;
  repeaters: Record<string, Record<string, unknown>[]>;
  matchedColumns: number;
  unmatchedHeaders: string[];
  duplicateHeaders: string[];
  errors: string[];
  missingRequired: string[];
}

export const fetchImportPgaFlagDefinitions = async (): Promise<{
  success: boolean;
  definitions: ImportPgaFlagDefinitionSummary[];
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getPgaFlagDefinitions();
  if (!success) {
    return {
      success: false,
      definitions: [],
      error:
        typeof result === 'string' ? result : 'Failed to load PGA definitions',
    };
  }
  return {
    success: true,
    definitions: Array.isArray(result?.definitions) ? result.definitions : [],
  };
};

export const fetchImportPgaFlagDefinition = async (
  code: PgaCode,
): Promise<{
  success: boolean;
  definition?: ImportPgaFlagDefinition;
  error?: string;
}> => {
  const cached = importPgaDefinitionCache.get(code);
  if (cached) return cached;
  const request = fetchImportPgaFlagDefinitionUncached(code);
  importPgaDefinitionCache.set(code, request);
  const result = await request;
  if (!result.success) importPgaDefinitionCache.delete(code);
  return result;
};

const importPgaDefinitionCache = new Map<
  PgaCode,
  Promise<{
    success: boolean;
    definition?: ImportPgaFlagDefinition;
    error?: string;
  }>
>();

const fetchImportPgaFlagDefinitionUncached = async (
  code: PgaCode,
): Promise<{
  success: boolean;
  definition?: ImportPgaFlagDefinition;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getPgaFlagDefinition(
    code,
  );
  if (!success) {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to load PGA definition',
    };
  }
  return { success: true, definition: result as ImportPgaFlagDefinition };
};

export const downloadImportPgaFlagTemplate = async (
  code: PgaCode,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } = await ProductServiceAPI.downloadPgaFlagTemplate(
    code,
  );
  if (!success || !(result instanceof Blob)) {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to download template',
    };
  }
  const url = URL.createObjectURL(result);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `pga-${code.toLowerCase()}-template.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { success: true };
};

export const previewImportPgaFlagTemplate = async (
  code: PgaCode,
  csvText: string,
): Promise<{
  success: boolean;
  preview?: ImportPgaTemplatePreview;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.previewPgaFlagTemplate(
    code,
    csvText,
  );
  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to read template',
    };
  }
  return {
    success: true,
    preview: {
      values: result?.values ?? {},
      repeaters: result?.repeaters ?? {},
      matchedColumns: Number(result?.matchedColumns ?? 0),
      unmatchedHeaders: Array.isArray(result?.unmatchedHeaders)
        ? result.unmatchedHeaders
        : [],
      duplicateHeaders: Array.isArray(result?.duplicateHeaders)
        ? result.duplicateHeaders
        : [],
      errors: Array.isArray(result?.errors) ? result.errors : [],
      missingRequired: [],
    },
  };
};

const toAppliedPgaFlag = (row: ImportServicePgaFlagRow): AppliedPgaFlag => ({
  code: row.code as PgaCode,
  addedAt: row.created_at,
  values: row.values ?? {},
  repeaters: row.repeaters ?? {},
  documents: row.documents ?? [],
});

export interface ImportPgaFlagsResult {
  success: boolean;
  flags: AppliedPgaFlag[];
  error?: string;
}

export const fetchImportPgaFlags = async (
  databaseProductId: string,
): Promise<ImportPgaFlagsResult> => {
  const { success, result } = await ProductServiceAPI.getProductPgaFlags(
    databaseProductId,
  );

  if (!success) {
    return {
      success: false,
      flags: [],
      error: typeof result === 'string' ? result : 'Failed to load flags',
    };
  }

  return {
    success: true,
    flags: Array.isArray(result) ? result.map(toAppliedPgaFlag) : [],
  };
};

export interface ImportPgaFlagWriteResult {
  success: boolean;
  error?: string;
}

// Always sends the flag's full current state (upsert semantics on the
// backend) — simpler than diffing, and matches how the editor already holds
// the whole values/repeaters/documents trio in memory before saving.
export const upsertImportPgaFlag = async (
  databaseProductId: string,
  flag: AppliedPgaFlag,
): Promise<ImportPgaFlagWriteResult> => {
  const { success, result } = await ProductServiceAPI.upsertProductPgaFlag(
    databaseProductId,
    flag.code,
    {
      values: flag.values,
      repeaters: flag.repeaters,
      documents: flag.documents,
    },
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to save flag',
    };
  }

  return { success: true };
};

export const deleteImportPgaFlag = async (
  databaseProductId: string,
  code: string,
): Promise<ImportPgaFlagWriteResult> => {
  const { success, result } = await ProductServiceAPI.deleteProductPgaFlag(
    databaseProductId,
    code,
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to remove flag',
    };
  }

  return { success: true };
};

export interface ImportScreeningRunResult {
  success: boolean;
  screeningResultId?: string;
  error?: string;
}

// Starts an async screening run (Kafka-enqueued server-side; the product's
// stored HTS code + country of origin are what the worker actually screens —
// not anything passed in this request). Resolves once the job is queued, not
// once it completes — see pollImportScreeningStatus for that.
export const runImportProductScreening = async (
  databaseId: string,
  databaseProductId: string,
): Promise<ImportScreeningRunResult> => {
  const { success, result } = await ProductServiceAPI.runSingleProductScreening(
    {
      databaseId,
      databaseProductId,
      // Import Hub doesn't screen manufacturers/vendors against
      // restricted-party lists.
      skipDeniedParty: true,
    },
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to start screening',
    };
  }

  const queuedRun = result?.result ?? result?.results ?? result;
  const screeningResultId = Array.isArray(queuedRun?.screeningResultIds)
    ? queuedRun.screeningResultIds.find(
        (value: unknown): value is string =>
          typeof value === 'string' && Boolean(value),
      )
    : undefined;

  return { success: true, screeningResultId };
};

export interface ImportScreeningPollResult {
  completed: boolean;
  timedOut: boolean;
}

// Polls the database status endpoint, but pins completion to the exact
// ScreeningResult created by this request. Without that guard, a prior completed
// run (or another concurrent run) can be mistaken for the product run that was
// just queued.
export const pollImportScreeningStatus = async (
  databaseId: string,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    screeningResultId?: string;
    isCancelled?: () => boolean;
  } = {},
): Promise<ImportScreeningPollResult> => {
  const intervalMs = options.intervalMs ?? 1500;
  const maxAttempts = options.maxAttempts ?? 80; // ~2 minutes at the default interval
  const isCancelled = options.isCancelled ?? (() => false);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isCancelled()) {
      return { completed: false, timedOut: false };
    }

    const { success, result } =
      await ProductServiceAPI.getDatabaseScreeningStatus(databaseId);
    const isExpectedRun =
      !options.screeningResultId ||
      result?.screeningResultId === options.screeningResultId;

    if (
      success &&
      result &&
      isExpectedRun &&
      !result.inProgress &&
      !result.aiInProgress &&
      !result.verificationInProgress
    ) {
      return { completed: true, timedOut: false };
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  return { completed: false, timedOut: true };
};

// Starts an async whole-database screening run (Kafka-enqueued server-side —
// every product gets its core compliance and tariff data refreshed). AI
// evaluation is intentionally reserved for Analyze Product. Mirrors the legacy
// Products Database tool's "Run Screening"
// button, which also fires this same request and discards the response,
// tracking progress purely via fetchImportDatabaseScreeningStatus below.
// tradeCategory: 'Imports' matches the existing cron precedent for
// import-direction product screening (due-product-screenings.ts).
export const runImportDatabaseScreening = async (
  databaseId: string,
  options: { runAiEvaluation?: boolean } = {},
): Promise<ImportScreeningRunResult> => {
  const { success, result } =
    await ProductServiceAPI.runProductDatabaseScreening({
      databaseIds: [databaseId],
      tradeCategory: 'Imports',
      skipDeniedParty: true,
      // Also run the per-product AI evaluation (Analyze Product) for every
      // product in the database.
      ...(options.runAiEvaluation ? { runAiEvaluation: true } : {}),
    });

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to start screening',
    };
  }

  return { success: true };
};

export interface ImportScreeningStatus {
  screeningResultId: string | null;
  databaseProductIds: string[];
  inProgress: boolean;
  screeningInProgress: boolean;
  finalizing: boolean;
  aiInProgress: boolean;
  /** True for Analyze Product and whole-database Run AI Evaluation runs. */
  aiRequested: boolean;
  verificationInProgress: boolean;
  verificationPhase: string | null;
  status: string;
  percent: number | null;
  chunkProcessed: number | null;
  chunkTotal: number | null;
  postChunkProcessed: number | null;
  /**
   * Chunks handed to Kafka so far. Below `chunkTotal` means the server is still
   * queueing work, which is a distinct phase from "queued, none processed yet".
   */
  publishedChunks: number | null;
  /**
   * Product-level counters. Preferred over chunk counters for display: chunk size
   * scales with database size, so "chunk 40 of 316" means nothing to a user while
   * "4,200 of 18,954 products" does. Null against a backend that predates them,
   * in which case the chunk counters are used instead.
   */
  totalProducts: number | null;
  productsProcessed: number | null;
  postProductsProcessed: number | null;
  postPercent: number | null;
  startedAt: string | null;
  /**
   * True only for the local optimistic state between pressing Run Screening and
   * the first poll that observes the new run. Distinguishes "starting" from a
   * previous run's terminal state, which would otherwise look like completion.
   */
  starting?: boolean;
}

export interface ImportScreeningStatusResult {
  success: boolean;
  status: ImportScreeningStatus | null;
  error?: string;
}

// Live status for a whole-database run — the same databaseId-keyed endpoint
// the legacy tool polls (getScreeningProgress needs a jobId this run never
// returns and reads a Redis namespace the product pipeline doesn't write —
// see the Slice 10 plan notes). Never-screened databases come back as
// `{ status: 'none', inProgress: false, ... }`, not an error.
export const fetchImportDatabaseScreeningStatus = async (
  databaseId: string,
): Promise<ImportScreeningStatusResult> => {
  const { success, result } =
    await ProductServiceAPI.getDatabaseScreeningStatus(databaseId);

  if (!success) {
    return {
      success: false,
      status: null,
      error:
        typeof result === 'string' ? result : 'Failed to load screening status',
    };
  }

  const run = result?.run ?? {};
  const num = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

  const chunkProcessed = num(run.chunkProcessed);
  const chunkTotal = num(run.chunkTotal);
  const percent =
    num(run.percent) ??
    (chunkTotal && chunkTotal > 0 && chunkProcessed != null
      ? Math.round((chunkProcessed / chunkTotal) * 100)
      : null);
  const postChunkProcessed = num(run.postChunkProcessed);
  const postPercent =
    num(run.postPercent) ??
    (chunkTotal && chunkTotal > 0 && postChunkProcessed != null
      ? Math.round((postChunkProcessed / chunkTotal) * 100)
      : null);

  return {
    success: true,
    status: {
      screeningResultId:
        typeof result?.screeningResultId === 'string'
          ? result.screeningResultId
          : null,
      databaseProductIds: Array.isArray(result?.databaseProductIds)
        ? result.databaseProductIds.filter(
            (value: unknown): value is string => typeof value === 'string',
          )
        : [],
      inProgress: !!result?.inProgress,
      screeningInProgress:
        typeof result?.screeningInProgress === 'boolean'
          ? result.screeningInProgress
          : !!result?.inProgress,
      finalizing: !!result?.finalizing,
      aiInProgress: !!result?.aiInProgress,
      // Default true against an older backend so its existing two-stage progress
      // behavior remains intact during a rolling frontend/backend deployment.
      aiRequested:
        typeof result?.aiRequested === 'boolean' ? result.aiRequested : true,
      verificationInProgress: !!result?.verificationInProgress,
      verificationPhase: result?.verificationPhase ?? null,
      status: result?.status ?? 'none',
      percent,
      chunkProcessed,
      chunkTotal,
      postChunkProcessed,
      // Defaults to chunkTotal so a backend without this field reads as "all
      // chunks queued" rather than stalling the UI in the queueing phase.
      publishedChunks: num(run.publishedChunks) ?? chunkTotal,
      totalProducts: num(run.totalProducts),
      productsProcessed: num(run.productsProcessed),
      postProductsProcessed: num(run.postProductsProcessed),
      postPercent,
      startedAt: typeof run.startedAt === 'string' ? run.startedAt : null,
    },
  };
};

export interface CreateImportDatabaseInput {
  name: string;
  groupId: string;
  createdBy: string;
  description?: string;
  icon?: string;
  accentColor?: string;
  subtype?: string;
  databaseStructure?: string;
  screeningFrequency?: string;
  screeningDayOfWeek?: string;
  notificationChannels?: string[];
}

export interface CreateImportDatabaseResult {
  success: boolean;
  database: ImportServiceDatabase | null;
  error?: string;
}

export const createImportDatabase = async (
  input: CreateImportDatabaseInput,
): Promise<CreateImportDatabaseResult> => {
  const notificationChannels = input.notificationChannels;
  const { success, result } = await ProductServiceAPI.createDatabase({
    name: input.name,
    group_id: input.groupId,
    created_by: input.createdBy,
    description: input.description,
    icon: input.icon,
    accent_color: input.accentColor,
    subtype: input.subtype,
    database_structure: input.databaseStructure,
    screening_frequency: input.screeningFrequency,
    screening_day_of_week: input.screeningDayOfWeek,
    notifications_email: notificationChannels?.join(','),
    notifications_enabled: notificationChannels
      ? notificationChannels.length > 0
      : undefined,
  });

  if (!success) {
    return {
      success: false,
      database: null,
      error: typeof result === 'string' ? result : 'Failed to create database',
    };
  }

  return { success: true, database: result ?? null };
};

export interface UpdateImportDatabaseSettingsInput {
  screeningFrequency: string;
  screeningDayOfWeek?: string | null;
  notificationChannels: string[];
  screeningLists: string[];
}

export const updateImportDatabaseSettings = async (
  databaseId: string,
  input: UpdateImportDatabaseSettingsInput,
): Promise<CreateImportDatabaseResult> => {
  const { success, result } = await ProductServiceAPI.updateDatabaseSettings(
    databaseId,
    {
      screening_frequency: input.screeningFrequency,
      screening_day_of_week:
        input.screeningFrequency === 'Weekly' ? input.screeningDayOfWeek : null,
      notifications_email: input.notificationChannels.join(','),
      notifications_enabled: input.notificationChannels.length > 0,
      screening_lists: input.screeningLists,
    },
  );

  if (!success) {
    return {
      success: false,
      database: null,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to update database settings',
    };
  }

  return { success: true, database: result ?? null };
};

export interface BulkUploadImportProductsResult {
  success: boolean;
  databaseId?: string;
  createdDatabase?: boolean;
  jobId?: string;
  status?: string;
  error?: string;
}

export interface ImportProductJobState {
  jobId: string;
  databaseId: string;
  groupId?: string;
  status:
    | 'queued'
    | 'validating'
    | 'importing'
    | 'finalizing'
    | 'completed'
    | 'failed'
    | 'cancelled';
  fileName: string;
  totalRows: number;
  validRows: number;
  processedRows: number;
  insertedRows: number;
  duplicateRows: number;
  invalidRows: number;
  pgaFlagsCreated?: number;
  errors: Array<{ row: number; message: string }>;
  warnings?: Array<{ row: number; message: string }>;
  hierarchyLinksCreated?: number;
  error?: string;
  errorCode?: 'PRODUCT_CAPACITY_EXCEEDED';
  databaseRemoved?: boolean;
  creditCheck?: {
    used: number;
    total: number;
    remaining: number;
    required: number;
  };
}

// Creates the database server-side too when the FormData carries no
// `database_id` — the same one-shot endpoint the legacy tool's file-based
// create flow uses (POST product/bulk-file-upload). Pass a `database_id`
// field on the FormData to import into an existing database instead
// (Slice B's Import Data action).
export const bulkUploadImportProducts = async (
  formData: FormData,
): Promise<BulkUploadImportProductsResult> => {
  const { success, result } = await ProductServiceAPI.bulkUploadProducts(
    formData,
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to upload products',
    };
  }

  return {
    success: true,
    databaseId: result?.databaseId ? String(result.databaseId) : undefined,
    createdDatabase: !!result?.createdDatabase,
    jobId: result?.jobId ? String(result.jobId) : undefined,
    status: result?.status ? String(result.status) : undefined,
  };
};

export const getImportProductJob = async (
  jobId: string,
): Promise<{
  success: boolean;
  job?: ImportProductJobState;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getProductImportJob(
    jobId,
  );
  if (!success) {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to load import status',
    };
  }
  return { success: true, job: result as ImportProductJobState };
};

export const waitForImportProductJob = async (
  jobId: string,
  options: {
    onProgress?: (job: ImportProductJobState) => void;
    isCancelled?: () => boolean;
    intervalMs?: number;
  } = {},
): Promise<ImportProductJobState | null> => {
  const intervalMs = Math.max(500, options.intervalMs ?? 1500);
  let consecutiveFailures = 0;

  // Ninety minutes accommodates very large uploads without polling forever.
  const maxAttempts = Math.ceil((90 * 60 * 1000) / intervalMs);
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options.isCancelled?.()) return null;
    const response = await getImportProductJob(jobId);
    if (!response.success || !response.job) {
      consecutiveFailures += 1;
      if (consecutiveFailures >= 3) {
        throw new Error(response.error ?? 'Failed to load import progress');
      }
    } else {
      consecutiveFailures = 0;
      options.onProgress?.(response.job);
      if (
        response.job.status === 'completed' ||
        response.job.status === 'failed' ||
        response.job.status === 'cancelled'
      ) {
        return response.job;
      }
    }
    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }

  throw new Error('Product import status timed out');
};

export interface CreateImportProductResult {
  success: boolean;
  productId?: string;
  error?: string;
}

export const createImportProduct = async (
  databaseId: string,
  fields: { name: string; [key: string]: unknown },
): Promise<CreateImportProductResult> => {
  // The product-service's INSERT explicitly lists created_at/updated_at as
  // columns rather than omitting them, so an explicit SQL NULL (what an
  // unset field becomes) never falls back to the column's own DEFAULT
  // now() -- every create fails with a NOT NULL violation unless the
  // caller supplies real timestamps. Reported upstream; this is the
  // workaround until that's fixed server-side.
  const now = new Date().toISOString();
  const { success, result } = await ProductServiceAPI.createProduct({
    ...fields,
    created_at: now,
    updated_at: now,
    database_id: databaseId,
  });

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to create product',
    };
  }

  return { success: true, productId: result?.product?.id };
};

export interface CurrentHtsOption {
  code: string;
  label: string;
  description: string;
}

export interface FindCurrentHtsCodesResult {
  success: boolean;
  options: CurrentHtsOption[];
  error?: string;
}

// Looks up ten-digit USHTS codes effective TODAY within the same 6-10 digit
// family as `prefix` -- used to offer a replacement when createProduct
// rejects a historical customs-entry HTS code as no longer in the current
// schedule (the entry's filing date can be well before today, and the
// product-service only accepts codes effective as of now). Pass the
// rejected code's own 8-digit family (`hts_code.replace(/\D/g, '').slice(0,
// 8)`) as `prefix` to find its still-valid sibling suffixes.
export const findCurrentHtsCodes = async (
  prefix: string,
  country?: string,
): Promise<FindCurrentHtsCodesResult> => {
  const { success, result } = await ProductServiceAPI.searchHtsCodes({
    prefix,
    country,
  });

  if (!success) {
    return {
      success: false,
      options: [],
      error: typeof result === 'string' ? result : 'Failed to search HTS codes',
    };
  }

  return { success: true, options: result?.options || [] };
};

export interface DeleteImportDatabaseResult {
  success: boolean;
  error?: string;
}

export const deleteImportDatabase = async (
  databaseId: string,
): Promise<DeleteImportDatabaseResult> => {
  const { success, result } = await ProductServiceAPI.deleteDatabase(
    databaseId,
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to delete database',
    };
  }

  return { success: true };
};

// ---------------------------------------------------------------------------
// Documents (reuses the legacy main-server /bite-db/uploads backend — the
// same "Uploads" (S3-backed) table the Products Database tool's product view
// uses, keyed by the same database_product_id. Deliberate choice: no
// biteservices work needed, and documents uploaded here show up in the
// legacy tool too, and vice versa.
// ---------------------------------------------------------------------------

export interface ImportServiceDocument {
  id: string;
  file_name: string;
  file_content_type: string;
  file_size: number;
  created_at: string;
  created_by?: string | null;
}

export interface FetchImportProductDocumentsResult {
  success: boolean;
  documents: ImportServiceDocument[];
  error?: string;
}

export const fetchImportProductDocuments = async (
  databaseProductId: string,
): Promise<FetchImportProductDocumentsResult> => {
  const { success, result } = await FileAPI.getUploadsByDatabaseProductId(
    databaseProductId,
    { limit: 100 },
  );

  if (!success) {
    return {
      success: false,
      documents: [],
      error: typeof result === 'string' ? result : 'Failed to load documents',
    };
  }

  return {
    success: true,
    documents: Array.isArray(result?.data) ? result.data : [],
  };
};

export interface UploadImportProductDocumentResult {
  success: boolean;
  error?: string;
}

export const uploadImportProductDocument = async (
  file: File,
  databaseId: string,
  databaseProductId: string,
): Promise<UploadImportProductDocumentResult> => {
  const { success, result } = await FileAPI.uploadFile(
    file,
    databaseId,
    databaseProductId,
  );

  if (!success) {
    return {
      success: false,
      error: typeof result === 'string' ? result : 'Failed to upload document',
    };
  }

  return { success: true };
};

// Mirrors the legacy UploadRow.tsx#download — fetch the file as a blob and
// trigger a browser download via a temporary anchor (the backend has no
// presigned-URL client flow; S3 access is server-side only).
export const downloadImportProductDocument = async (
  uploadId: string,
  fileName: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } = await FileAPI.downloadUploadById(uploadId);

  if (!success || !result) {
    return {
      success: false,
      error:
        typeof result === 'string' ? result : 'Failed to download document',
    };
  }

  const url = window.URL.createObjectURL(result as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true };
};

export type ImportProductExportJobStatus =
  | 'queued'
  | 'preparing'
  | 'generating'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type ImportProductExportMode = 'source-aware' | 'generated';
export type ImportProductExportFormat = 'cargowise-xml' | 'pdf-report';

export interface ImportProductExportWarning {
  code: string;
  message: string;
  productId?: string;
  externalId?: string;
  productName?: string;
  partNumber?: string;
  tariffCode?: string;
}

export interface ImportProductExportSummary {
  totalSourceRecords: number;
  matchedProducts: number;
  enrichedProducts: number;
  unchangedProducts: number;
  unmatchedSourceRecords: number;
  productsWithoutSource: number;
  unscreenedProducts: number;
  staleTariffRowsPreserved: number;
  tariffRowsCleared?: number;
  tariffRowsMarkedForDeletion?: number;
  unsupportedPgaFlags: number;
  generatedProducts?: number;
}

export interface ImportProductExportJob {
  jobId: string;
  databaseId: string;
  status: ImportProductExportJobStatus;
  fileName: string;
  format?: ImportProductExportFormat;
  mode: ImportProductExportMode | 'report';
  processedProducts: number;
  totalProducts: number;
  uploadId?: string;
  s3Location?: string;
  resultFileName?: string;
  summary?: ImportProductExportSummary;
  warnings: ImportProductExportWarning[];
  warningCount: number;
  error?: string;
}

export const createImportCargoWiseExport = async (input: {
  databaseId: string;
  fileName: string;
  productIds?: string[];
}): Promise<{
  success: boolean;
  jobId?: string;
  mode?: ImportProductExportMode;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.createCargoWiseExportJob(
    input,
  );
  if (!success || !result?.success || !result?.jobId) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : result?.error ?? 'Failed to start CargoWise XML export',
    };
  }
  return {
    success: true,
    jobId: String(result.jobId),
    mode: result.mode === 'generated' ? 'generated' : 'source-aware',
  };
};

export interface ImportCargoWiseExportReadiness {
  total: number;
  unscreened: number;
}

export const fetchImportCargoWiseExportReadiness = async (input: {
  databaseId: string;
  productIds?: string[];
}): Promise<{
  success: boolean;
  readiness?: ImportCargoWiseExportReadiness;
  error?: string;
}> => {
  const { success, result } =
    await ProductServiceAPI.getCargoWiseExportReadiness(input);
  if (!success || !result?.success) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : result?.error ?? 'Failed to check XML export readiness',
    };
  }
  return {
    success: true,
    readiness: {
      total: Number(result.total) || 0,
      unscreened: Number(result.unscreened) || 0,
    },
  };
};

export const createImportPdfReport = async (input: {
  databaseId: string;
  fileName: string;
  productIds?: string[];
  filters?: ImportProductListQuery;
}): Promise<{
  success: boolean;
  jobId?: string;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.createImportPdfReportJob(
    {
      ...input,
      filters: input.filters ? { ...input.filters } : undefined,
    },
  );
  if (!success || !result?.success || !result?.jobId) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : result?.error ?? 'Failed to start PDF report export',
    };
  }
  return { success: true, jobId: String(result.jobId) };
};

export const fetchImportProductExportJob = async (
  jobId: string,
): Promise<{
  success: boolean;
  job?: ImportProductExportJob;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getProductExportJob(
    jobId,
  );
  const job = result?.data as ImportProductExportJob | undefined;
  if (!success || !result?.success || !job) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : result?.error ?? 'Failed to load export status',
    };
  }
  return { success: true, job };
};

export const downloadImportProductExport = downloadImportProductDocument;

export const downloadImportPdfReport = async (
  jobId: string,
  fileName: string,
): Promise<{ success: boolean; error?: string }> => {
  const { success, result } = await ProductServiceAPI.downloadImportPdfReport(
    jobId,
  );
  if (!success || !result) {
    return {
      success: false,
      error:
        typeof result === 'string'
          ? result
          : 'Failed to download PDF report',
    };
  }
  const url = window.URL.createObjectURL(result as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`,
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
  return { success: true };
};

// ---------------------------------------------------------------------------
// Comments (reuses the legacy main-server /bite-db/comment backend — the
// same "Comment" table the Products Database tool's product view uses,
// commentable_id = database_product_id. Note: comment.service.ts's functions
// return null/[]/false directly rather than a {success,result} envelope, and
// carry no error detail — these wrappers do their best with that shape.
// ---------------------------------------------------------------------------

export interface ImportServiceComment {
  id: string;
  content: string;
  author_id?: string;
  author_name?: string;
  created_at?: string;
}

export interface FetchImportProductCommentsResult {
  success: boolean;
  comments: ImportServiceComment[];
}

export const fetchImportProductComments = async (
  databaseProductId: string,
): Promise<FetchImportProductCommentsResult> => {
  const comments = await getCommentsByCommentableId(
    databaseProductId,
    'DatabaseProduct',
  );
  return { success: true, comments: comments as ImportServiceComment[] };
};

export interface AddImportProductCommentInput {
  databaseProductId: string;
  content: string;
  authorId?: string;
  authorName?: string;
}

export interface AddImportProductCommentResult {
  success: boolean;
  comment?: ImportServiceComment;
  error?: string;
}

export const addImportProductComment = async (
  input: AddImportProductCommentInput,
): Promise<AddImportProductCommentResult> => {
  const comment = await createComment({
    commentable_type: 'DatabaseProduct',
    commentable_id: input.databaseProductId,
    content: input.content,
    author_id: input.authorId,
    author_name: input.authorName,
  });

  if (!comment) {
    return { success: false, error: 'Failed to add comment' };
  }

  return { success: true, comment: comment as ImportServiceComment };
};

export const deleteImportProductComment = async (
  commentId: string,
): Promise<{ success: boolean; error?: string }> => {
  const ok = await deleteComment(commentId);
  if (!ok) {
    return { success: false, error: 'Failed to delete comment' };
  }
  return { success: true };
};
